import React, { JSX, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  BuildingStorefrontIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PhotoIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { useAuth } from 'context/user'
import { AccountOverlayLinkState, useAccountOverlayLink } from 'hooks/useAccountOverlayLink'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { SavedNote } from 'components/SavedNote'
import { ALLOWED_IMAGE_TYPES, ImageUpload, imageFileProblem, uploadImageAsset } from 'components/ImageUpload'
import { AssetPickerModal } from 'components/AssetPickerModal'
import { ColorInput } from 'components/ColorPickers'
import { graphqlRequest } from 'lib/helpers'
import { ADMIN_PATH } from 'lib/paths'
import { sortByName } from 'lib/elementOrder'
import {
  MAX_MERCH_BOARD_PAD_X_PX,
  MAX_MERCH_COLLAPSE_X_PX,
  MAX_MERCH_CORNER_RADIUS,
  MAX_MERCH_OUTER_BORDER_PX,
  MAX_MERCH_PHOTO_HEIGHT_PX,
  MAX_MERCH_TEXT_GLOW_PX,
  MAX_MERCH_TWEEN_MS,
  MERCH_FONT_OPTION_GROUPS,
  MERCH_STYLE_DEFAULTS,
  MIN_MERCH_COLLAPSE_X_PX,
  MIN_MERCH_PHOTO_HEIGHT_PX,
  MIN_MERCH_TWEEN_MS,
  MerchChangeAnimation,
  ResolvedMerchStyle,
  merchChangeAnimationOr,
  merchCollapseXSizeOr,
  merchColorOr,
  merchOuterBorderOr,
  merchPadXOr,
  merchPhotoHeightOrNull,
  merchRadiusOr,
  merchTextGlowPxOr,
  merchTweenMsOr,
  normalizeMerchFontKey
} from 'lib/merchStyle'
import { MerchTestModeControls } from 'components/MerchTestModeControls'
import { MERCH_BOARD_NATURAL_WIDTH, MerchBoardPanel, MerchBoardPanelRow } from 'components/MerchBoardPanel'
import {
  MERCH_TICKER_MARQUEE_WIDTH,
  MerchTickerEnvelopeMessage,
  MerchTickerMarquee,
  MerchTickerMarqueeEnvelope,
  MerchTickerMarqueeProduct
} from 'components/MerchTickerMarquee'
import {
  emptyTickerMessage,
  MAX_TICKER_BORDER_PX,
  MAX_TICKER_GAP_PX,
  MAX_TICKER_LABEL_LEN,
  MAX_TICKER_LINE_HEIGHT_PX,
  MAX_TICKER_MESSAGES,
  MAX_TICKER_PAD_PX,
  MAX_TICKER_PCT_SLOT_PCT,
  MAX_TICKER_PRICE_SLOT_CENTS,
  MAX_TICKER_SCROLL_SPEED_PX_S,
  MAX_TICKER_TEXT_GLOW_PX,
  MAX_TICKER_TEXT_LEN,
  MAX_TICKER_TEXT_SIZE_PX,
  MERCH_TICKER_STYLE_DEFAULTS,
  MIN_TICKER_LINE_HEIGHT_PX,
  MIN_TICKER_SCROLL_SPEED_PX_S,
  MIN_TICKER_TEXT_SIZE_PX,
  MerchDisplayStyle,
  merchDisplayStyleOf,
  MerchTickerLabelColor,
  MerchTickerMessageDraft,
  MerchTickerStyleDraft,
  resolveTickerMessages,
  resolveTickerStyleDraft
} from 'lib/merchTickerConfig'
import { ensureFontLoaded } from 'lib/fontLoader'
import {
  DeletionResult,
  MerchBoardData,
  MerchBoardMode,
  MerchItem,
  MerchMoveDirection,
  MerchMoveResult,
  MerchOverlaySummary,
  MerchVariantOption,
  MirroredShopifyProduct,
  ShopifyProduct,
  ShopifyShop
} from 'types/api'
import {
  SectionCard,
  FieldLabel,
  CopyButton,
  OVERLAY_LINK_NOTE,
  OverlayLinkBlock,
  OverlayLinkRowActions,
  QuickOverlayManagerView
} from 'components/chatOverlays/ChatOverlayManager'
import createMerchOverlayMutation from 'graphql/createMerchOverlay.graphql'
import updateMerchOverlayMutation from 'graphql/updateMerchOverlay.graphql'
import updateMerchTickerMutation from 'graphql/updateMerchTicker.graphql'
import updateMerchOverlayVisibilityMutation from 'graphql/updateMerchOverlayVisibility.graphql'
import deleteMerchOverlayMutation from 'graphql/deleteMerchOverlay.graphql'
import listMyMerchOverlaysQuery from 'graphql/listMyMerchOverlays.graphql'
import listMyShopifyShopsQuery from 'graphql/listMyShopifyShops.graphql'
import listShopifyProductsQuery from 'graphql/listShopifyProducts.graphql'
import mirrorShopifyProductImageMutation from 'graphql/mirrorShopifyProductImage.graphql'
import moveMerchBoardItemMutation from 'graphql/moveMerchBoardItem.graphql'
import getMerchBoardDataByIdQuery from 'graphql/getMerchBoardDataById.graphql'

const MAX_MERCH_ITEMS = 10
export const MAX_ITEM_IMAGE_URLS = 10
const MAX_TITLE_LEN = 80
const MAX_LINK_LEN = 512
const MAX_SHOP_LABEL_LEN = 40
const MAX_CTA_TEXT_LEN = 60
const DEFAULT_MERCH_SHOP_LABEL = '[ SHOP ]'
const MAX_PRICE_CENTS = 10_000_000
const PRODUCT_ID_RE = /^[0-9]{1,20}$/
export const MAX_VARIANT_OPTIONS = 12
export const MAX_VARIANT_LABEL_LEN = 40
const VARIANT_ID_RE = /^[0-9]{1,24}$/

const priceLabel = (cents: number | null): string =>
  cents === null ? 'no price' : `$${(cents / 100).toFixed(2)}`

export const merchExtensionVisibleOf = (overlay: Pick<MerchOverlaySummary, 'extensionVisible'>): boolean =>
  overlay.extensionVisible !== false

export const merchAutoReorderOf = (overlay: Pick<MerchOverlaySummary, 'autoReorder'>): boolean =>
  overlay.autoReorder !== false

export const MAX_EXTENSION_DELAY_MS = 60_000

const clampExtensionDelayMs = (ms: number): number =>
  Math.min(MAX_EXTENSION_DELAY_MS, Math.max(0, Math.round(ms)))

export const merchExtensionDelayMsOf = (overlay: Pick<MerchOverlaySummary, 'extensionDelayMs'>): number => {
  const raw = overlay.extensionDelayMs
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return 0
  return clampExtensionDelayMs(raw)
}

export const MIN_EXTENSION_START_WIDTH_PCT = 5
export const MAX_EXTENSION_START_WIDTH_PCT = 60

export const merchExtensionStartCollapsedOf = (overlay: Pick<MerchOverlaySummary, 'extensionStartCollapsed'>): boolean =>
  overlay.extensionStartCollapsed === true

export const merchExtensionStartWidthPctOf = (overlay: Pick<MerchOverlaySummary, 'extensionStartWidthPct'>): number | null => {
  const raw = overlay.extensionStartWidthPct
  return typeof raw === 'number' && Number.isFinite(raw) &&
    raw >= MIN_EXTENSION_START_WIDTH_PCT && raw <= MAX_EXTENSION_START_WIDTH_PCT
    ? raw
    : null
}

export const formatExtensionDelaySeconds = (ms: number): string => (ms / 1000).toFixed(3)

export const merchExtensionChannelLoginOf = (
  overlay: Pick<MerchOverlaySummary, 'extensionChannelLogin'>
): string => typeof overlay.extensionChannelLogin === 'string' ? overlay.extensionChannelLogin : ''

export const merchExtensionChannelIdOf = (
  overlay: Pick<MerchOverlaySummary, 'extensionChannelId'>
): string => typeof overlay.extensionChannelId === 'string' ? overlay.extensionChannelId : ''

export const parseExtensionDelaySeconds = (text: string): number | 'invalid' => {
  const trimmed = text.trim()
  if (trimmed === '') return 0
  const seconds = Number(trimmed)
  if (!Number.isFinite(seconds) || seconds < 0 || Object.is(seconds, -0)) return 'invalid'
  return clampExtensionDelayMs(seconds * 1000)
}

const parsePriceUsd = (text: string): number | null | 'invalid' => {
  const trimmed = text.trim()
  if (trimmed === '') return null
  const usd = Number(trimmed)
  if (!Number.isFinite(usd) || usd < 0) return 'invalid'
  const cents = Math.round(usd * 100)
  if (cents > MAX_PRICE_CENTS) return 'invalid'
  return cents
}

const parseBasePriceUsd = (text: string): number | null | 'invalid' => {
  const trimmed = text.trim()
  if (trimmed === '') return null
  const usd = Number(trimmed)
  if (!Number.isFinite(usd)) return 'invalid'
  const cents = Math.round(usd * 100)
  if (cents <= 0 || cents > MAX_PRICE_CENTS) return 'invalid'
  return cents
}

const parseLinkText = (text: string): string | null | 'invalid' => {
  const trimmed = text.trim()
  if (trimmed === '') return null
  if (!trimmed.startsWith('https://') || trimmed.length > MAX_LINK_LEN) return 'invalid'
  return trimmed
}

const sortItemsByLadder = (items: MerchItem[], order: string[]): MerchItem[] => {
  const pos = new Map(order.map((id, i) => [id, i]))
  return [...items].sort((a, b) =>
    (pos.get(a.productId) ?? order.length) - (pos.get(b.productId) ?? order.length)
  )
}

export const itemPhotoUrls = (item: MerchItem): string[] => {
  const list = (item.imageUrls != null && item.imageUrls.length > 0)
    ? item.imageUrls
    : (item.imageUrl !== null ? [item.imageUrl] : [])
  return Array.from(new Set(list))
}

export const appendItemPhoto = (item: MerchItem, url: string): MerchItem => {
  const current = itemPhotoUrls(item)
  const imageUrl = item.imageUrl ?? url
  if (current.includes(url)) return item.imageUrl === imageUrl ? item : { ...item, imageUrl }
  if (current.length >= MAX_ITEM_IMAGE_URLS) return item
  return { ...item, imageUrl, imageUrls: [...current, url] }
}

export const removeItemPhoto = (item: MerchItem, url: string): MerchItem => {
  const current = itemPhotoUrls(item)
  const index = current.indexOf(url)
  if (index === -1) return item
  const remaining = current.filter((u) => u !== url)
  const next = remaining[Math.min(index, remaining.length - 1)] ?? null
  return {
    ...item,
    imageUrl: item.imageUrl === url ? next : item.imageUrl,
    imageUrls: remaining.length > 0 ? remaining : null
  }
}

export interface MerchOverlayDraft {
  name: string
  storeTitle: string
  mode: MerchBoardMode
  shopDomain: string
  extensionVisible: boolean
  extensionDelayMs: number
  autoReorder: boolean
  extensionStartCollapsed: boolean
  extensionStartWidthPct: number | null
  extensionChannelLogin: string
  shopMode: boolean
  shopLabelText: string
  ctaText: string
  items: MerchItem[]
  titleColor: string
  titleFontKey: string
  productTitleColor: string
  productTitleFontKey: string
  priceFontKey: string
  boardBgColor: string
  rowBgColor: string
  tileBgColor: string
  outerBgColor: string
  outerCornerRadius: number
  boardCornerRadius: number
  outerBorderColor: string
  outerBorderWidth: number
  tabOutlineColor: string
  tabTextColor: string
  changeAnimation: MerchChangeAnimation
  tweenMs: number
  showVariants: boolean
  squarePhotos: boolean
  offsiteMarkColor: string
  textGlowPx: number
  textGlowColor: string
  textGlowArrows: boolean
  outerPadX: number
  boardPadX: number
  shopLabelColor: string
  ctaColor: string
  outerPadTop: number
  outerPadBottom: number
  boardBgImage: string
  productImageHeight: number | null
  rowBorderColor: string
  collapseXSize: number
  collapseXColor: string
  messages: MerchTickerMessageDraft[]
  tickerStyle: MerchTickerStyleDraft
}

const emptyDraft = (): MerchOverlayDraft => ({
  name: '',
  storeTitle: '',
  mode: 'PRICES',
  shopDomain: '',
  extensionVisible: true,
  extensionDelayMs: 0,
  autoReorder: true,
  extensionStartCollapsed: false,
  extensionStartWidthPct: null,
  extensionChannelLogin: '',
  shopMode: false,
  shopLabelText: DEFAULT_MERCH_SHOP_LABEL,
  ctaText: '',
  items: [],
  titleColor: MERCH_STYLE_DEFAULTS.titleColor,
  titleFontKey: MERCH_STYLE_DEFAULTS.titleFontKey,
  productTitleColor: MERCH_STYLE_DEFAULTS.productTitleColor,
  productTitleFontKey: MERCH_STYLE_DEFAULTS.productTitleFontKey,
  priceFontKey: MERCH_STYLE_DEFAULTS.priceFontKey,
  boardBgColor: MERCH_STYLE_DEFAULTS.boardBgColor,
  rowBgColor: MERCH_STYLE_DEFAULTS.rowBgColor,
  tileBgColor: MERCH_STYLE_DEFAULTS.tileBgColor,
  outerBgColor: MERCH_STYLE_DEFAULTS.outerBgColor,
  outerCornerRadius: MERCH_STYLE_DEFAULTS.outerCornerRadius,
  boardCornerRadius: MERCH_STYLE_DEFAULTS.boardCornerRadius,
  outerBorderColor: MERCH_STYLE_DEFAULTS.outerBorderColor,
  outerBorderWidth: MERCH_STYLE_DEFAULTS.outerBorderWidth,
  tabOutlineColor: MERCH_STYLE_DEFAULTS.tabOutlineColor,
  tabTextColor: MERCH_STYLE_DEFAULTS.tabTextColor,
  changeAnimation: MERCH_STYLE_DEFAULTS.changeAnimation,
  tweenMs: MERCH_STYLE_DEFAULTS.tweenMs,
  showVariants: MERCH_STYLE_DEFAULTS.showVariants,
  squarePhotos: MERCH_STYLE_DEFAULTS.squarePhotos,
  offsiteMarkColor: MERCH_STYLE_DEFAULTS.offsiteMarkColor,
  textGlowPx: MERCH_STYLE_DEFAULTS.textGlowPx,
  textGlowColor: MERCH_STYLE_DEFAULTS.textGlowColor,
  textGlowArrows: MERCH_STYLE_DEFAULTS.textGlowArrows,
  outerPadX: MERCH_STYLE_DEFAULTS.outerPadX,
  boardPadX: MERCH_STYLE_DEFAULTS.boardPadX,
  shopLabelColor: MERCH_STYLE_DEFAULTS.shopLabelColor,
  ctaColor: MERCH_STYLE_DEFAULTS.ctaColor,
  outerPadTop: MERCH_STYLE_DEFAULTS.outerPadTop,
  outerPadBottom: MERCH_STYLE_DEFAULTS.outerPadBottom,
  boardBgImage: MERCH_STYLE_DEFAULTS.boardBgImage,
  productImageHeight: MERCH_STYLE_DEFAULTS.productImageHeight,
  rowBorderColor: MERCH_STYLE_DEFAULTS.rowBorderColor,
  collapseXSize: MERCH_STYLE_DEFAULTS.collapseXSize,
  collapseXColor: MERCH_STYLE_DEFAULTS.collapseXColor,
  messages: [],
  tickerStyle: { ...MERCH_TICKER_STYLE_DEFAULTS }
})

const draftFromOverlay = (overlay: MerchOverlaySummary): MerchOverlayDraft => ({
  name: overlay.name,
  storeTitle: overlay.storeTitle,
  mode: overlay.mode === 'ORDERS' ? 'ORDERS' : 'PRICES',
  shopDomain: overlay.shopDomain ?? '',
  extensionVisible: merchExtensionVisibleOf(overlay),
  extensionDelayMs: merchExtensionDelayMsOf(overlay),
  autoReorder: merchAutoReorderOf(overlay),
  extensionStartCollapsed: merchExtensionStartCollapsedOf(overlay),
  extensionStartWidthPct: merchExtensionStartWidthPctOf(overlay),
  extensionChannelLogin: merchExtensionChannelLoginOf(overlay),
  shopMode: overlay.shopMode === true,
  shopLabelText: typeof overlay.shopLabelText === 'string' ? overlay.shopLabelText : DEFAULT_MERCH_SHOP_LABEL,
  ctaText: typeof overlay.ctaText === 'string' ? overlay.ctaText : '',
  items: overlay.items.map((i) => ({ ...i })),
  titleColor: merchColorOr(overlay.style?.titleColor, MERCH_STYLE_DEFAULTS.titleColor),
  titleFontKey: normalizeMerchFontKey(overlay.style?.titleFontKey),
  productTitleColor: merchColorOr(overlay.style?.productTitleColor, MERCH_STYLE_DEFAULTS.productTitleColor),
  productTitleFontKey: normalizeMerchFontKey(overlay.style?.productTitleFontKey),
  priceFontKey: normalizeMerchFontKey(overlay.style?.priceFontKey),
  boardBgColor: merchColorOr(overlay.style?.boardBgColor, MERCH_STYLE_DEFAULTS.boardBgColor),
  rowBgColor: merchColorOr(overlay.style?.rowBgColor, MERCH_STYLE_DEFAULTS.rowBgColor),
  tileBgColor: merchColorOr(overlay.style?.tileBgColor, MERCH_STYLE_DEFAULTS.tileBgColor),
  outerBgColor: merchColorOr(overlay.style?.outerBgColor, MERCH_STYLE_DEFAULTS.outerBgColor),
  outerCornerRadius: merchRadiusOr(overlay.style?.outerCornerRadius, MERCH_STYLE_DEFAULTS.outerCornerRadius),
  boardCornerRadius: merchRadiusOr(overlay.style?.boardCornerRadius, MERCH_STYLE_DEFAULTS.boardCornerRadius),
  outerBorderColor: merchColorOr(overlay.style?.outerBorderColor, MERCH_STYLE_DEFAULTS.outerBorderColor),
  outerBorderWidth: merchOuterBorderOr(overlay.style?.outerBorderWidth, MERCH_STYLE_DEFAULTS.outerBorderWidth),
  tabOutlineColor: merchColorOr(overlay.style?.tabOutlineColor, MERCH_STYLE_DEFAULTS.tabOutlineColor),
  tabTextColor: merchColorOr(overlay.style?.tabTextColor, MERCH_STYLE_DEFAULTS.tabTextColor),
  changeAnimation: merchChangeAnimationOr(overlay.style?.changeAnimation, MERCH_STYLE_DEFAULTS.changeAnimation),
  tweenMs: merchTweenMsOr(overlay.style?.tweenMs, MERCH_STYLE_DEFAULTS.tweenMs),
  showVariants: overlay.style?.showVariants !== false,
  squarePhotos: overlay.style?.squarePhotos === true,
  offsiteMarkColor: merchColorOr(overlay.style?.offsiteMarkColor, MERCH_STYLE_DEFAULTS.offsiteMarkColor),
  textGlowPx: merchTextGlowPxOr(overlay.style?.textGlowPx, MERCH_STYLE_DEFAULTS.textGlowPx),
  textGlowColor: merchColorOr(overlay.style?.textGlowColor, MERCH_STYLE_DEFAULTS.textGlowColor),
  textGlowArrows: overlay.style?.textGlowArrows !== false,
  outerPadX: merchPadXOr(overlay.style?.outerPadX, MERCH_STYLE_DEFAULTS.outerPadX),
  boardPadX: merchPadXOr(overlay.style?.boardPadX, MERCH_STYLE_DEFAULTS.boardPadX),
  shopLabelColor: merchColorOr(overlay.style?.shopLabelColor, MERCH_STYLE_DEFAULTS.shopLabelColor),
  ctaColor: merchColorOr(overlay.style?.ctaColor, MERCH_STYLE_DEFAULTS.ctaColor),
  outerPadTop: merchPadXOr(overlay.style?.outerPadTop, MERCH_STYLE_DEFAULTS.outerPadTop),
  outerPadBottom: merchPadXOr(overlay.style?.outerPadBottom, MERCH_STYLE_DEFAULTS.outerPadBottom),
  boardBgImage: typeof overlay.style?.boardBgImage === 'string' ? overlay.style.boardBgImage : '',
  productImageHeight: merchPhotoHeightOrNull(overlay.style?.productImageHeight),
  rowBorderColor: merchColorOr(overlay.style?.rowBorderColor, MERCH_STYLE_DEFAULTS.rowBorderColor),
  collapseXSize: merchCollapseXSizeOr(overlay.style?.collapseXSize, MERCH_STYLE_DEFAULTS.collapseXSize),
  collapseXColor: merchColorOr(overlay.style?.collapseXColor, MERCH_STYLE_DEFAULTS.collapseXColor),
  messages: resolveTickerMessages(overlay.messages),
  tickerStyle: resolveTickerStyleDraft(overlay.tickerStyle)
})

const ShopifyProductPickerModal = ({
  shop,
  auth0Token,
  existingIds,
  atCap,
  photoless,
  onAdd,
  onClose
}: {
  shop: string
  auth0Token: string
  existingIds: Set<string>
  atCap: boolean
  photoless: boolean
  onAdd: (item: MerchItem) => void
  onClose: () => void
}): JSX.Element => {
  const [products, setProducts] = useState<ShopifyProduct[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [addingId, setAddingId] = useState<string | null>(null)

  const load = useCallback(async (search: string): Promise<void> => {
    setProducts(null)
    setError(null)
    try {
      const trimmed = search.trim()
      const { products: fetched } = await graphqlRequest<{ products: ShopifyProduct[] }>(
        listShopifyProductsQuery,
        trimmed === '' ? { shop } : { shop, query: trimmed },
        auth0Token
      )
      setProducts(fetched)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products')
      setProducts([])
    }
  }, [shop, auth0Token])

  useEffect(() => {
    void load('')
  }, [load])

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    globalThis.addEventListener('keydown', onKey)
    return () => globalThis.removeEventListener('keydown', onKey)
  }, [onClose])

  const handleAdd = async (product: ShopifyProduct): Promise<void> => {
    if (addingId !== null) return
    if (existingIds.has(product.productId) || atCap) return
    if (photoless) {
      onAdd({
        productId: product.productId,
        title: product.title.slice(0, MAX_TITLE_LEN),
        priceCents: product.priceCents,
        imageUrl: null,
        imageUrls: null,
        linkUrl: product.linkUrl,
        defaultVariantId: null,
        variantOptionName: null,
        variantOptions: []
      })
      return
    }
    setAddingId(product.productId)
    setError(null)
    try {
      const { mirrored } = await graphqlRequest<{ mirrored: MirroredShopifyProduct }>(
        mirrorShopifyProductImageMutation,
        { shop, productId: product.productId },
        auth0Token
      )
      const allUrls = (mirrored.assets ?? []).map((a) => a.publicUrl)
      const refused = (product.realOptionCount ?? 0) >= 2
      onAdd({
        productId: mirrored.productId,
        title: mirrored.title.slice(0, MAX_TITLE_LEN),
        priceCents: mirrored.priceCents,
        imageUrl: mirrored.asset.publicUrl,
        imageUrls: allUrls.length > 0 ? allUrls : [mirrored.asset.publicUrl],
        linkUrl: mirrored.linkUrl,
        defaultVariantId: mirrored.defaultVariantId ?? null,
        variantOptionName: refused
          ? (mirrored.variantOptionName ?? 'Options')
          : (mirrored.variantOptionName ?? null),
        variantOptions: refused ? [] : (mirrored.variantOptions ?? [])
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add the product')
    } finally {
      setAddingId(null)
    }
  }

  let body: JSX.Element
  if (products === null) {
    body = (
      <div className='flex items-center justify-center py-12'>
        <LoadingSpinner />
      </div>
    )
  } else if (products.length === 0) {
    body = (
      <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/10 px-4 py-10 text-center text-sm text-dm-text-secondary'>
        <BuildingStorefrontIcon className='size-8 text-dm-text-accent' aria-hidden='true' />
        <p>No products found{query.trim() !== '' ? ' for that search' : ' in this store'}.</p>
      </div>
    )
  } else {
    body = (
      <ul className='max-h-[60vh] space-y-1.5 overflow-y-auto' data-testid='shopify-product-picker-list'>
        {products.map((product) => {
          const added = existingIds.has(product.productId)
          const busy = addingId === product.productId
          return (
            <li
              key={product.productId}
              className='flex items-center gap-3 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2'
            >
              {!photoless && (
                <div className='flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-white/10 bg-white'>
                  {product.imageUrl !== null
                    ? <img src={product.imageUrl} alt='' loading='lazy' className='size-full object-contain' />
                    : <BuildingStorefrontIcon className='size-5 text-zinc-500' aria-hidden='true' />}
                </div>
              )}
              <div className='flex min-w-0 flex-1 flex-col leading-tight'>
                <span className='truncate text-sm font-medium text-dm-text-primary'>{product.title}</span>
                <span className='truncate text-[11px] text-dm-text-accent'>
                  {priceLabel(product.priceCents)} · id {product.productId}
                  {!photoless && (product.images?.length ?? 0) > 1 && ` · ${product.images?.length ?? 0} photos`}
                </span>
                {(product.realOptionCount ?? 0) >= 2 && (
                  <span className='text-[11px] text-amber-300'>
                    Too many variant types, incompatible, add this product manually.
                  </span>
                )}
              </div>
              <button
                type='button'
                onClick={() => { void handleAdd(product) }}
                disabled={added || busy || atCap || addingId !== null}
                title={added ? 'Already on this board' : 'Add to the board'}
                className='inline-flex shrink-0 items-center gap-1.5 rounded-md bg-brand px-2.5 py-1 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
              >
                {busy ? <LoadingSpinner /> : <PlusIcon className='size-3.5' aria-hidden='true' />}
                {added ? 'Added' : 'Add'}
              </button>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <div
      data-testid='shopify-product-picker-modal'
      className='fixed inset-0 z-50 flex items-center justify-center p-4'
    >
      <div className='absolute inset-0 bg-black/70' onClick={onClose} aria-hidden='true' />
      <div className='relative w-full max-w-2xl rounded-xl border border-white/10 bg-dm-bg-secondary p-5 shadow-xl'>
        <div className='mb-4 flex items-center justify-between gap-3'>
          <h3 className='text-base font-semibold text-dm-text-primary'>
            Add from your store
          </h3>
          <button
            type='button'
            onClick={onClose}
            title='Close'
            className='rounded-md p-1.5 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary'
          >
            <XMarkIcon className='size-4' aria-hidden='true' />
            <span className='sr-only'>Close</span>
          </button>
        </div>
        <div className='mb-3 flex gap-1.5'>
          <input
            type='text'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return
              e.preventDefault()
              void load(query)
            }}
            placeholder='Search products…'
            autoComplete='off'
            className='min-w-0 flex-1 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary placeholder:text-dm-text-accent focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
          />
          <button
            type='button'
            onClick={() => { void load(query) }}
            className='inline-flex shrink-0 items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
          >
            <MagnifyingGlassIcon className='size-3.5' aria-hidden='true' />
            Search
          </button>
        </div>
        {error !== null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>
          </div>
        )}
        {atCap && (
          <p className='mb-3 text-[11px] text-amber-300'>
            This board is at its {MAX_MERCH_ITEMS}-item cap, remove an item
            to add another.
          </p>
        )}
        {body}
      </div>
    </div>
  )
}

const MerchPreview = ({ draft }: { draft: MerchOverlayDraft }): JSX.Element => {
  useEffect(() => {
    void ensureFontLoaded(draft.titleFontKey)
    void ensureFontLoaded(draft.productTitleFontKey)
    void ensureFontLoaded(draft.priceFontKey)
  }, [draft.titleFontKey, draft.productTitleFontKey, draft.priceFontKey])

  const hostRef = useRef<HTMLDivElement | null>(null)
  const tickerRef = useRef<HTMLDivElement | null>(null)
  const [fit, setFit] = useState<{ scale: number, height: number, width: number } | null>(null)

  useLayoutEffect(() => {
    const host = hostRef.current
    const ticker = tickerRef.current
    if (host === null || ticker === null) return
    const refit = (): void => {
      const paneW = host.offsetWidth
      const naturalH = ticker.offsetHeight
      const naturalW = ticker.offsetWidth
      if (paneW <= 0 || naturalH <= 0 || naturalW <= 0) return
      const scale = Math.min(1, paneW / naturalW)
      setFit({ scale, height: Math.ceil(naturalH * scale), width: naturalW })
    }
    refit()
    const ro = new ResizeObserver(refit)
    ro.observe(host)
    ro.observe(ticker)
    void document.fonts?.ready?.then(() => refit())
    return () => ro.disconnect()
  }, [])

  const style: ResolvedMerchStyle = {
    titleColor: draft.titleColor,
    titleFontKey: draft.titleFontKey,
    productTitleColor: draft.productTitleColor,
    productTitleFontKey: draft.productTitleFontKey,
    priceFontKey: draft.priceFontKey,
    boardBgColor: draft.boardBgColor,
    rowBgColor: draft.rowBgColor,
    tileBgColor: draft.tileBgColor,
    outerBgColor: draft.outerBgColor,
    outerCornerRadius: draft.outerCornerRadius,
    boardCornerRadius: draft.boardCornerRadius,
    outerBorderColor: draft.outerBorderColor,
    outerBorderWidth: draft.outerBorderWidth,
    tabOutlineColor: draft.tabOutlineColor,
    tabTextColor: draft.tabTextColor,
    changeAnimation: draft.changeAnimation,
    tweenMs: draft.tweenMs,
    showVariants: draft.showVariants,
    squarePhotos: draft.squarePhotos,
    offsiteMarkColor: draft.offsiteMarkColor,
    textGlowPx: draft.textGlowPx,
    textGlowColor: draft.textGlowColor,
    textGlowArrows: draft.textGlowArrows,
    outerPadX: draft.outerPadX,
    boardPadX: draft.boardPadX,
    shopLabelColor: draft.shopLabelColor,
    ctaColor: draft.ctaColor,
    outerPadTop: draft.outerPadTop,
    outerPadBottom: draft.outerPadBottom,
    boardBgImage: draft.boardBgImage,
    productImageHeight: draft.productImageHeight,
    rowBorderColor: draft.rowBorderColor,
    collapseXSize: draft.collapseXSize,
    collapseXColor: draft.collapseXColor
  }

  const rows: MerchBoardPanelRow[] = draft.items.map((item) => ({
    productId: item.productId,
    title: item.title,
    priceCents: item.priceCents,
    imageUrl: item.imageUrl,
    linkUrl: item.linkUrl,
    trend: null,
    variants: item.variantOptions ?? []
  }))

  return (
    <div className='overflow-hidden rounded-lg border border-white/10 bg-gradient-to-br from-zinc-700 via-zinc-800 to-zinc-600 p-3'>
      <div ref={hostRef} className='relative overflow-hidden' style={{ height: fit === null ? undefined : fit.height }}>
        <div
          style={{
            width: fit === null ? MERCH_BOARD_NATURAL_WIDTH : Math.ceil(fit.width * fit.scale),
            height: fit === null ? undefined : fit.height,
            margin: '0 auto',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              width: fit === null ? MERCH_BOARD_NATURAL_WIDTH : fit.width,
              height: fit === null ? undefined : Math.ceil(fit.height / fit.scale),
              transform: fit === null ? undefined : `scale(${fit.scale})`,
              transformOrigin: 'top left'
            }}
          >
            <MerchBoardPanel
              storeTitle={draft.storeTitle.trim() === '' ? 'STORE TITLE' : draft.storeTitle}
              rows={rows}
              style={style}
              shopMode={draft.shopMode}
              shopLabel={draft.shopLabelText}
              ctaText={draft.ctaText}
              fillHost
              tickerRef={tickerRef}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

const TICKER_PREVIEW_SAMPLE_QUOTES: ReadonlyArray<{ trend: string | null, pctLabel: string }> = [
  { trend: 'up', pctLabel: '+5.4%' },
  { trend: 'down', pctLabel: '-2.2%' },
  { trend: null, pctLabel: '0.0%' }
]

interface TickerPreviewSourceProduct {
  productId: string
  title: string
  priceCents: number | null
}

const TICKER_PREVIEW_PLACEHOLDER_PRODUCTS: readonly TickerPreviewSourceProduct[] = [
  { productId: 'preview-tee', title: 'Tour Tee', priceCents: 3500 },
  { productId: 'preview-hoodie', title: 'Summit Hoodie', priceCents: 6500 },
  { productId: 'preview-cap', title: 'Dad Cap', priceCents: 2800 }
]

const TICKER_PREVIEW_PLACEHOLDER_MESSAGES: readonly MerchTickerEnvelopeMessage[] = [
  { id: 'preview-msg-1', label: 'NEW DROP', labelColor: 'green', text: 'Tour edition just landed at the merch stand' },
  { id: 'preview-msg-2', label: 'LAST CALL', labelColor: 'red', text: 'Limited run — gone when the show ends' },
  { id: 'preview-msg-3', label: 'MERCH', labelColor: 'white', text: 'Stand is open until the encore' },
  { id: 'preview-msg-4', label: 'HIDDEN', labelColor: 'hide', text: 'A message with no heading at all' }
]

export const buildTickerPreviewEnvelope = (
  items: MerchItem[],
  messages: MerchTickerMessageDraft[],
  tickerStyle: MerchTickerStyleDraft
): MerchTickerMarqueeEnvelope => {
  const source: readonly TickerPreviewSourceProduct[] = items.length > 0
    ? items.map((item) => ({
      productId: item.productId,
      title: item.title,
      priceCents: item.basePriceCents ?? item.priceCents
    }))
    : TICKER_PREVIEW_PLACEHOLDER_PRODUCTS
  const products: MerchTickerMarqueeProduct[] = source.map((p, i) => {
    const sample = TICKER_PREVIEW_SAMPLE_QUOTES[i % TICKER_PREVIEW_SAMPLE_QUOTES.length]
    return {
      productId: p.productId,
      title: p.title,
      priceCents: p.priceCents,
      trend: sample.trend,
      pctLabel: sample.pctLabel
    }
  })
  const drafted: MerchTickerEnvelopeMessage[] = messages
    .filter((m) => m.label.trim() !== '' || m.text.trim() !== '')
    .map((m) => ({ id: m.key, label: m.label, labelColor: m.labelColor, text: m.text }))
  return {
    products,
    messages: drafted.length > 0 ? drafted : [...TICKER_PREVIEW_PLACEHOLDER_MESSAGES],
    tickerStyle: resolveTickerStyleDraft(tickerStyle),
    deltas: {}
  }
}

export const MerchTickerPreview = ({ draft }: { draft: MerchOverlayDraft }): JSX.Element => {
  useEffect(() => {
    void ensureFontLoaded(draft.tickerStyle.fontKey)
  }, [draft.tickerStyle.fontKey])

  const hostRef = useRef<HTMLDivElement | null>(null)
  const stageRef = useRef<HTMLDivElement | null>(null)
  const [fit, setFit] = useState<{ scale: number, height: number } | null>(null)

  useLayoutEffect(() => {
    const host = hostRef.current
    const stage = stageRef.current
    if (host === null || stage === null) return
    const refit = (): void => {
      const paneW = host.offsetWidth
      const naturalH = stage.offsetHeight
      if (paneW <= 0 || naturalH <= 0) return
      const scale = Math.min(1, paneW / MERCH_TICKER_MARQUEE_WIDTH)
      const height = Math.ceil(naturalH * scale)
      setFit((prev) => prev !== null && prev.scale === scale && prev.height === height ? prev : { scale, height })
    }
    refit()
    const ro = new ResizeObserver(refit)
    ro.observe(host)
    ro.observe(stage)
    void document.fonts?.ready?.then(() => refit())
    return () => ro.disconnect()
  }, [])

  const envelope = useMemo(
    () => buildTickerPreviewEnvelope(draft.items, draft.messages, draft.tickerStyle),
    [draft.items, draft.messages, draft.tickerStyle]
  )

  return (
    <div
      className='overflow-hidden rounded-lg border border-white/10 bg-gradient-to-br from-zinc-700 via-zinc-800 to-zinc-600 p-3'
      data-testid='merch-ticker-preview'
    >
      <div ref={hostRef} className='relative overflow-hidden' style={{ height: fit === null ? undefined : fit.height }}>
        <div
          ref={stageRef}
          style={{
            width: MERCH_TICKER_MARQUEE_WIDTH,
            transform: fit === null ? undefined : `scale(${fit.scale})`,
            transformOrigin: 'top left'
          }}
        >
          <MerchTickerMarquee envelope={envelope} />
        </div>
      </div>
    </div>
  )
}

interface ManualItemState {
  title: string
  productId: string
  priceText: string
  linkUrl: string
  imageUrl: string
}

const emptyManualItem = (): ManualItemState => ({
  title: '',
  productId: '',
  priceText: '',
  linkUrl: '',
  imageUrl: ''
})

const ManualItemAdd = ({
  existingIds,
  atCap,
  showPhoto,
  onAdd
}: {
  existingIds: Set<string>
  atCap: boolean
  showPhoto: boolean
  onAdd: (item: MerchItem) => void
}): JSX.Element => {
  const [item, setItem] = useState<ManualItemState>(emptyManualItem())
  const [error, setError] = useState<string | null>(null)

  const inputClass = 'w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary placeholder:text-dm-text-accent focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'

  const handleAdd = (): void => {
    const title = item.title.trim()
    const productId = item.productId.trim()
    const linkUrl = parseLinkText(item.linkUrl)
    if (title === '' || title.length > MAX_TITLE_LEN) {
      setError(`Name is required (max ${MAX_TITLE_LEN} characters).`)
      return
    }
    if (!PRODUCT_ID_RE.test(productId)) {
      setError('Product ID must be the numeric Shopify product id (digits only) sales are matched on it.')
      return
    }
    if (existingIds.has(productId)) {
      setError('That product id is already on this board.')
      return
    }
    const priceCents = parsePriceUsd(item.priceText)
    if (priceCents === 'invalid') {
      setError('Price must be a non-negative USD amount (or empty for no price).')
      return
    }
    if (linkUrl === 'invalid') {
      setError(`Link must be an https URL (max ${MAX_LINK_LEN} characters), or empty.`)
      return
    }
    setError(null)
    onAdd({
      productId,
      title,
      priceCents,
      imageUrl: item.imageUrl === '' ? null : item.imageUrl,
      linkUrl
    })
    setItem(emptyManualItem())
  }

  return (
    <div className='mt-4 rounded-lg border border-white/5 bg-dm-bg-accent/20 p-3'>
      <FieldLabel>Add an item manually</FieldLabel>
      <div className='grid grid-cols-1 gap-2 md:grid-cols-2'>
        <input
          value={item.title}
          onChange={(e) => setItem((m) => ({ ...m, title: e.target.value }))}
          placeholder='Product name'
          maxLength={MAX_TITLE_LEN}
          className={inputClass}
        />
        <input
          value={item.productId}
          onChange={(e) => setItem((m) => ({ ...m, productId: e.target.value }))}
          placeholder='Shopify product id (digits)'
          autoComplete='off'
          className={inputClass}
        />
        <input
          value={item.priceText}
          onChange={(e) => setItem((m) => ({ ...m, priceText: e.target.value }))}
          placeholder='Price in USD (optional)'
          inputMode='decimal'
          autoComplete='off'
          className={inputClass}
        />
        <input
          value={item.linkUrl}
          onChange={(e) => setItem((m) => ({ ...m, linkUrl: e.target.value }))}
          placeholder='https:// product link (optional)'
          maxLength={MAX_LINK_LEN}
          autoComplete='off'
          className={inputClass}
        />
      </div>
      {showPhoto && (
        <div className='mt-2' data-testid='manual-item-photo'>
          <ImageUpload
            value={item.imageUrl}
            onChange={(next) => setItem((m) => ({ ...m, imageUrl: next }))}
            label='Photo (optional)'
            helpText='The board shows initials when empty.'
          />
        </div>
      )}
      {error !== null && (
        <div className='mt-2'>
          <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>
        </div>
      )}
      <button
        type='button'
        onClick={handleAdd}
        disabled={atCap}
        title={atCap ? `This board is at its ${MAX_MERCH_ITEMS}-item cap` : undefined}
        className='mt-2 inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
      >
        <PlusIcon className='size-3.5' aria-hidden='true' />
        Add item
      </button>
    </div>
  )
}

const MerchBasePriceInput = ({
  itemTitle,
  valueCents,
  onCommit
}: {
  itemTitle: string
  valueCents: number | null
  onCommit: (cents: number | null) => void
}): JSX.Element => {
  const [text, setText] = useState(valueCents === null ? '' : (valueCents / 100).toFixed(2))
  const parsed = parseBasePriceUsd(text)
  return (
    <label className='flex flex-wrap items-center gap-2'>
      <span className='text-[10px] font-medium uppercase tracking-wider text-dm-text-accent'>
        Base price (USD)
      </span>
      <input
        value={text}
        onChange={(e) => {
          const next = e.target.value
          setText(next)
          const cents = parseBasePriceUsd(next)
          onCommit(cents === 'invalid' ? null : cents)
        }}
        inputMode='decimal'
        autoComplete='off'
        placeholder='12.00'
        className='w-24 rounded-md border border-white/10 bg-dm-bg-accent/40 px-2 py-1 text-xs text-dm-text-primary placeholder:text-dm-text-accent focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
      />
      <span className='sr-only'>Base price for {itemTitle}</span>
      {parsed === 'invalid' && (
        <span className='text-[11px] text-amber-300'>
          Must be a positive USD amount (or empty for no baseline).
        </span>
      )}
    </label>
  )
}

const MerchLinkInput = ({
  itemTitle,
  value,
  onCommit
}: {
  itemTitle: string
  value: string | null
  onCommit: (linkUrl: string | null) => void
}): JSX.Element => {
  const [text, setText] = useState(value ?? '')
  const parsed = parseLinkText(text)
  return (
    <label className='flex min-w-0 flex-1 flex-wrap items-center gap-2'>
      <span className='text-[10px] font-medium uppercase tracking-wider text-dm-text-accent'>
        Link
      </span>
      <input
        value={text}
        onChange={(e) => {
          const next = e.target.value
          setText(next)
          const linkUrl = parseLinkText(next)
          if (linkUrl !== 'invalid') onCommit(linkUrl)
        }}
        inputMode='url'
        autoComplete='off'
        maxLength={MAX_LINK_LEN}
        placeholder='https://… (optional)'
        className='min-w-48 flex-1 rounded-md border border-white/10 bg-dm-bg-accent/40 px-2 py-1 text-xs text-dm-text-primary placeholder:text-dm-text-accent focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
      />
      <span className='sr-only'>Link for {itemTitle}</span>
      {parsed === 'invalid' && (
        <span className='text-[11px] text-amber-300'>
          Must be an https URL (max {MAX_LINK_LEN} characters) or empty, the
          previous link is kept until this is fixed.
        </span>
      )}
    </label>
  )
}

interface MerchVariantRowDraft {
  key: string
  id: string
  label: string
  link: string
}

let merchVariantRowSeq = 0
const nextVariantRowKey = (): string => {
  merchVariantRowSeq += 1
  return `vrow-${merchVariantRowSeq}`
}

export const parseVariantRows = (rows: MerchVariantRowDraft[]): MerchVariantOption[] | null => {
  const seen = new Set<string>()
  const out: MerchVariantOption[] = []
  for (const r of rows) {
    const id = r.id.trim()
    const label = r.label.trim()
    const linkUrl = parseLinkText(r.link)
    if (!VARIANT_ID_RE.test(id) || seen.has(id)) return null
    if (label === '' || label.length > MAX_VARIANT_LABEL_LEN) return null
    if (linkUrl === 'invalid') return null
    seen.add(id)
    out.push({ id, label, linkUrl })
  }
  return out
}

const MerchVariantsEditor = ({
  item,
  merchOverlayId,
  auth0Token,
  cartBase,
  onCommit
}: {
  item: MerchItem
  merchOverlayId: string | null
  auth0Token: string | undefined
  cartBase: string
  onCommit: (variantOptions: MerchVariantOption[]) => void
}): JSX.Element => {
  const [rows, setRows] = useState<MerchVariantRowDraft[]>(() =>
    (item.variantOptions ?? []).map((v) => ({
      key: nextVariantRowKey(),
      id: v.id,
      label: v.label,
      link: v.linkUrl ?? ''
    }))
  )
  const [importBusy, setImportBusy] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  const atCap = rows.length >= MAX_VARIANT_OPTIONS

  const applyRows = (next: MerchVariantRowDraft[]): void => {
    setRows(next)
    const parsed = parseVariantRows(next)
    if (parsed !== null) onCommit(parsed)
  }

  const patchRow = (key: string, patch: Partial<MerchVariantRowDraft>): void => {
    applyRows(rows.map((r) => r.key === key ? { ...r, ...patch } : r))
  }

  const handleImport = async (): Promise<void> => {
    if (merchOverlayId === null || auth0Token === undefined || importBusy) return
    setImportBusy(true)
    setImportError(null)
    try {
      const { merchBoard } = await graphqlRequest<{ merchBoard: MerchBoardData }>(
        getMerchBoardDataByIdQuery, { merchOverlayId }, auth0Token
      )
      let envelope: unknown = null
      if (typeof merchBoard?.board === 'string' && merchBoard.board !== '') {
        try { envelope = JSON.parse(merchBoard.board) } catch { envelope = null }
      }
      const products = (envelope as { products?: unknown })?.products
      if (!Array.isArray(products)) {
        setImportError('The live board has no composed envelope yet, save the board with a bound store first.')
        return
      }
      const product = products.find((p: unknown) =>
        (p as { productId?: unknown })?.productId === item.productId) as { variants?: unknown } | undefined
      if (product === undefined) {
        setImportError('The live board does not have this product yet. Save the board with this product bound, then import variants.')
        return
      }
      const fetched: Array<{ id: string, label: string }> = []
      if (Array.isArray(product.variants)) {
        for (const v of product.variants) {
          const id = (v as { id?: unknown })?.id
          const label = (v as { label?: unknown })?.label
          if (typeof id === 'string' && typeof label === 'string' && id !== '' && label !== '') {
            fetched.push({ id, label: label.slice(0, MAX_VARIANT_LABEL_LEN) })
          }
        }
      }
      if (fetched.length === 0) {
        setImportError('The live board carries no variants for this item.')
        return
      }
      if (rows.length > 0) {
        const ok = globalThis.confirm(
          `Replace the ${rows.length} variant row${rows.length === 1 ? '' : 's'} for "${item.title}" with the ${fetched.length} on the live board? Links on matching ids are kept.`
        )
        if (!ok) return
      }
      const linkById = new Map(rows.map((r) => [r.id.trim(), r.link]))
      applyRows(fetched.slice(0, MAX_VARIANT_OPTIONS).map((v) => ({
        key: nextVariantRowKey(),
        id: v.id,
        label: v.label,
        link: linkById.get(v.id) ?? ''
      })))
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Failed to load the live board')
    } finally {
      setImportBusy(false)
    }
  }

  const idCounts = new Map<string, number>()
  for (const r of rows) {
    const id = r.id.trim()
    if (id !== '') idCounts.set(id, (idCounts.get(id) ?? 0) + 1)
  }

  const rowInputClass = 'rounded-md border border-white/10 bg-dm-bg-accent/40 px-2 py-1 text-xs text-dm-text-primary placeholder:text-dm-text-accent focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
  let importTitle: string
  if (merchOverlayId === null) {
    importTitle = 'Save the board first the import reads the live board by its id'
  } else if (importBusy) {
    importTitle = 'Loading the live board…'
  } else {
    importTitle = 'Prefill these rows from the live board’s current variants'
  }

  return (
    <div className='mt-2 border-t border-white/5 pt-2' data-testid={`merch-variants-editor-${item.productId}`}>
      <div className='flex flex-wrap items-center gap-2'>
        <span className='text-[10px] font-medium uppercase tracking-wider text-dm-text-accent'>
          Variants ({rows.length}/{MAX_VARIANT_OPTIONS})
        </span>
        <button
          type='button'
          onClick={() => { void handleImport() }}
          disabled={merchOverlayId === null || auth0Token === undefined || importBusy}
          title={importTitle}
          data-testid={`import-variants-button-${item.productId}`}
          className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-[11px] font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-50'
        >
          {importBusy ? <LoadingSpinner /> : <ArrowDownTrayIcon className='size-3.5' aria-hidden='true' />}
          Import variants from live board
        </button>
        <button
          type='button'
          onClick={() => applyRows([...rows, { key: nextVariantRowKey(), id: '', label: '', link: '' }])}
          disabled={atCap}
          title={atCap ? `This item is at its ${MAX_VARIANT_OPTIONS}-variant cap` : 'Add a variant row'}
          data-testid={`add-variant-button-${item.productId}`}
          className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-[11px] font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-50'
        >
          <PlusIcon className='size-3.5' aria-hidden='true' />
          Add variant
        </button>
      </div>
      {importError !== null && (
        <div className='mt-2'>
          <ErrorInline onDismiss={() => setImportError(null)}>{importError}</ErrorInline>
        </div>
      )}
      {rows.length > 0 && (
        <div className='mt-1.5 flex flex-col gap-1.5'>
          {rows.map((r) => {
            const id = r.id.trim()
            const idInvalid = !VARIANT_ID_RE.test(id)
            const idDup = !idInvalid && (idCounts.get(id) ?? 0) > 1
            const labelBlank = r.label.trim() === ''
            const labelLong = r.label.trim().length > MAX_VARIANT_LABEL_LEN
            const linkInvalid = parseLinkText(r.link) === 'invalid'
            return (
              <div key={r.key} data-testid={`merch-variant-row-${item.productId}`}>
                <div className='flex flex-wrap items-center gap-1.5'>
                  <input
                    value={r.id}
                    onChange={(e) => patchRow(r.key, { id: e.target.value })}
                    placeholder='Variant id'
                    inputMode='numeric'
                    autoComplete='off'
                    maxLength={24}
                    aria-label={`Variant id for ${item.title}`}
                    data-testid='merch-variant-id-input'
                    className={`${rowInputClass} w-32`}
                  />
                  <input
                    value={r.label}
                    onChange={(e) => patchRow(r.key, { label: e.target.value })}
                    placeholder='Name (S / M / L…)'
                    autoComplete='off'
                    maxLength={MAX_VARIANT_LABEL_LEN}
                    aria-label={`Variant name for ${item.title}`}
                    data-testid='merch-variant-label-input'
                    className={`${rowInputClass} w-32`}
                  />
                  <input
                    value={r.link}
                    onChange={(e) => patchRow(r.key, { link: e.target.value })}
                    placeholder={idInvalid ? 'https://… (optional)' : `${cartBase}${id}:1 (default)`}
                    inputMode='url'
                    autoComplete='off'
                    maxLength={MAX_LINK_LEN}
                    aria-label={`Override link for ${item.title} ${r.label === '' ? 'unnamed variant' : r.label}`}
                    data-testid='merch-variant-link-input'
                    className={`${rowInputClass} min-w-48 flex-1`}
                  />
                  <button
                    type='button'
                    onClick={() => applyRows(rows.filter((x) => x.key !== r.key))}
                    title='Remove this variant'
                    data-testid='merch-variant-remove-button'
                    className='shrink-0 rounded-md p-1.5 text-dm-text-accent hover:bg-red-500/10 hover:text-red-300'
                  >
                    <TrashIcon className='size-4' aria-hidden='true' />
                    <span className='sr-only'>Remove variant {r.label === '' ? '' : r.label} of {item.title}</span>
                  </button>
                </div>
                {(idInvalid || idDup || labelBlank || labelLong || linkInvalid) && (
                  <p className='mt-0.5 text-[11px] text-amber-300' data-testid='merch-variant-row-hint'>
                    {idInvalid && 'Variant id must be digits only (1–24 characters) the numeric Shopify variant id. '}
                    {idDup && 'Duplicate variant id on this item. '}
                    {labelBlank && `Name is required (max ${MAX_VARIANT_LABEL_LEN} characters). `}
                    {labelLong && `Name is too long (max ${MAX_VARIANT_LABEL_LEN} characters). `}
                    {linkInvalid && `Link must be an https URL (max ${MAX_LINK_LEN} characters) or empty. `}
                    The last valid list is kept until this is fixed.
                  </p>
                )}
              </div>
            )
          })}
        </div>
      )}
      <p className='mt-1 text-[11px] text-dm-text-accent'>
        The option boxes shoppers see on hover, in this order (boxes render
        with 2 or more). On a live-feed board these replace the feed&apos;s
        variants for this product — leave the list empty to use the
        feed&apos;s. An empty link uses the store&apos;s cart permalink for that
        variant.
      </p>
    </div>
  )
}

const MerchItemTitleInput = ({
  productId,
  value,
  onCommit
}: {
  productId: string
  value: string
  onCommit: (title: string) => void
}): JSX.Element => {
  const [text, setText] = useState(value)
  const blank = text.trim() === ''
  return (
    <label className='flex min-w-0 flex-1 flex-wrap items-center gap-2'>
      <span className='text-[10px] font-medium uppercase tracking-wider text-dm-text-accent'>
        Name
      </span>
      <input
        value={text}
        onChange={(e) => {
          const next = e.target.value
          setText(next)
          const trimmed = next.trim()
          if (trimmed !== '') onCommit(trimmed)
        }}
        maxLength={MAX_TITLE_LEN}
        autoComplete='off'
        placeholder='Product name'
        className='min-w-40 flex-1 rounded-md border border-white/10 bg-dm-bg-accent/40 px-2 py-1 text-xs text-dm-text-primary placeholder:text-dm-text-accent focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
      />
      <span className='sr-only'>Name for product {productId}</span>
      {blank && (
        <span className='text-[11px] text-amber-300'>
          Name can&apos;t be empty, the previous name is kept until this is
          fixed.
        </span>
      )}
    </label>
  )
}

export const MerchItemPhotoStrip = ({
  item,
  auth0Token,
  onFeature,
  onAdded,
  onRemove,
  onUploadingChange
}: {
  item: MerchItem
  auth0Token: string | undefined
  onFeature: (url: string) => void
  onAdded: (url: string) => void
  onRemove: (url: string) => void
  onUploadingChange: (uploading: boolean) => void
}): JSX.Element => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [uploading, setUploading] = useState(false)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const photos = itemPhotoUrls(item)
  const atPhotoCap = photos.length >= MAX_ITEM_IMAGE_URLS
  const canUpload = auth0Token !== undefined && !atPhotoCap && !uploading

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if ((file == null) || auth0Token === undefined || atPhotoCap || uploading) return
    const problem = imageFileProblem(file)
    if (problem !== null) {
      setError(problem)
      return
    }
    setUploading(true)
    onUploadingChange(true)
    setError(null)
    try {
      const { upload } = await uploadImageAsset(file, auth0Token)
      onAdded(upload.publicUrl)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
      onUploadingChange(false)
    }
  }

  let tileTitle: string
  if (uploading) {
    tileTitle = 'Uploading…'
  } else if (atPhotoCap) {
    tileTitle = `This item is at its ${MAX_ITEM_IMAGE_URLS}-photo cap`
  } else {
    tileTitle = 'Add a photo (PNG, JPEG, WebP, or GIF)'
  }
  const libraryTitle = atPhotoCap
    ? `This item is at its ${MAX_ITEM_IMAGE_URLS}-photo cap`
    : 'Choose from uploads'

  return (
    <div className='mt-2 border-t border-white/5 pt-2' data-testid={`merch-item-photos-${item.productId}`}>
      <div className='flex flex-wrap items-center gap-1.5'>
        <span className='mr-1 text-[10px] font-medium uppercase tracking-wider text-dm-text-accent'>
          Photos
        </span>
        {photos.map((url) => (
          <div key={url} className='relative shrink-0'>
            <button
              type='button'
              onClick={() => onFeature(url)}
              title={url === item.imageUrl ? 'Featured on the overlay' : 'Feature this photo'}
              data-testid={`photo-tile-${item.productId}`}
              data-photo-url={url}
              data-featured={url === item.imageUrl ? 'true' : 'false'}
              className={
                url === item.imageUrl
                  ? 'flex size-9 items-center justify-center overflow-hidden rounded-md bg-white ring-2 ring-brand'
                  : 'flex size-9 items-center justify-center overflow-hidden rounded-md bg-white ring-1 ring-white/10 opacity-70 hover:opacity-100'
              }
            >
              <img src={url} alt='' loading='lazy' className='size-full object-contain' />
              <span className='sr-only'>
                {url === item.imageUrl ? 'Featured photo' : `Feature this photo of ${item.title}`}
              </span>
            </button>
            <button
              type='button'
              onClick={() => onRemove(url)}
              title='Remove this photo'
              data-testid={`remove-photo-button-${item.productId}`}
              data-photo-url={url}
              className='absolute -right-1.5 -top-1.5 flex size-4 items-center justify-center rounded-full bg-zinc-900 text-dm-text-secondary ring-1 ring-white/20 hover:bg-red-500/80 hover:text-white'
            >
              <XMarkIcon className='size-3' aria-hidden='true' />
              <span className='sr-only'>Remove this photo of {item.title}</span>
            </button>
          </div>
        ))}
        <input
          ref={fileInputRef}
          type='file'
          accept={ALLOWED_IMAGE_TYPES.join(',')}
          onChange={(e) => { void handleFile(e) }}
          className='hidden'
          data-testid={`add-photo-input-${item.productId}`}
        />
        <button
          type='button'
          onClick={() => fileInputRef.current?.click()}
          disabled={!canUpload}
          title={tileTitle}
          data-testid={`add-photo-button-${item.productId}`}
          className='flex size-9 items-center justify-center rounded-md border border-dashed border-white/20 text-dm-text-accent hover:border-white/40 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-50'
        >
          {uploading ? <LoadingSpinner /> : <PlusIcon className='size-4' aria-hidden='true' />}
          <span className='sr-only'>
            {uploading ? `Uploading a photo of ${item.title}` : `Add a photo of ${item.title}`}
          </span>
        </button>
        <button
          type='button'
          onClick={() => setLibraryOpen(true)}
          disabled={!canUpload}
          title={libraryTitle}
          data-testid={`choose-photo-button-${item.productId}`}
          className='flex size-9 items-center justify-center rounded-md border border-dashed border-white/20 text-dm-text-accent hover:border-white/40 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-50'
        >
          <PhotoIcon className='size-4' aria-hidden='true' />
          <span className='sr-only'>Choose a photo of {item.title} from your uploads</span>
        </button>
        {uploading && (
          <span className='text-[11px] text-dm-text-accent' role='status'>Uploading…</span>
        )}
        {!uploading && atPhotoCap && (
          <span className='text-[11px] text-dm-text-accent' data-testid={`photo-cap-hint-${item.productId}`}>
            {MAX_ITEM_IMAGE_URLS}-photo cap reached.
          </span>
        )}
      </div>
      {error !== null && (
        <div className='mt-2'>
          <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>
        </div>
      )}
      {libraryOpen && (
        <AssetPickerModal
          onClose={() => setLibraryOpen(false)}
          onSelect={(publicUrl) => {
            onAdded(publicUrl)
            setLibraryOpen(false)
          }}
        />
      )}
    </div>
  )
}

const merchSelectClass = 'w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'

const MerchFontSelect = ({
  label,
  value,
  onChange
}: {
  label: string
  value: string
  onChange: (next: string) => void
}): JSX.Element => (
  <label className='block'>
    <FieldLabel>{label}</FieldLabel>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={merchSelectClass}
    >
      {MERCH_FONT_OPTION_GROUPS.map((group) => (
        <optgroup key={group.category} label={group.label}>
          {group.options.map((o) => (
            <option key={o.key} value={o.key}>{o.label}</option>
          ))}
        </optgroup>
      ))}
    </select>
  </label>
)

const MerchRadiusInput = ({
  label,
  value,
  onChange
}: {
  label: string
  value: number
  onChange: (next: number) => void
}): JSX.Element => (
  <label className='block'>
    <FieldLabel>{label}</FieldLabel>
    <input
      type='number'
      inputMode='numeric'
      min={0}
      max={MAX_MERCH_CORNER_RADIUS}
      step={1}
      value={value}
      onChange={(e) => {
        const parsed = Number(e.target.value)
        if (!Number.isFinite(parsed)) return
        onChange(Math.min(MAX_MERCH_CORNER_RADIUS, Math.max(0, Math.round(parsed))))
      }}
      className={merchSelectClass}
    />
  </label>
)

const MERCH_CHANGE_ANIMATION_OPTIONS: ReadonlyArray<{
  value: MerchChangeAnimation
  title: string
  copy: string
}> = [
  { value: 'FLASH', title: 'Color flash', copy: 'The current green/red highlight on changed items.' },
  { value: 'BLINK', title: 'Blink', copy: 'Price, arrow and percentage blink three times on change. Product names never blink.' },
  { value: 'TWEEN', title: 'Count up/down', copy: 'The number rolls smoothly from the old value to the new one. No flash, no blink. The arrow and its color change straight away.' },
  { value: 'NONE', title: 'None', copy: 'Values just update in place.' }
]

export const formatMerchTweenSeconds = (ms: number): string => (ms / 1000).toFixed(3)

export const parseMerchTweenSeconds = (text: string): number | 'invalid' => {
  const trimmed = text.trim()
  if (trimmed === '') return 'invalid'
  const seconds = Number(trimmed)
  if (!Number.isFinite(seconds) || seconds < 0 || Object.is(seconds, -0)) return 'invalid'
  const ms = Math.round(seconds * 1000)
  if (ms < MIN_MERCH_TWEEN_MS || ms > MAX_MERCH_TWEEN_MS) return 'invalid'
  return ms
}

const MerchTweenDurationInput = ({
  valueMs,
  onCommit
}: {
  valueMs: number
  onCommit: (ms: number) => void
}): JSX.Element => {
  const [text, setText] = useState(formatMerchTweenSeconds(valueMs))
  const [badInput, setBadInput] = useState(false)
  const invalid = badInput || parseMerchTweenSeconds(text) === 'invalid'
  return (
    <div className='mt-3' data-testid='merch-tween-duration-block'>
      <FieldLabel>Count duration (seconds)</FieldLabel>
      <input
        type='number'
        inputMode='decimal'
        min={MIN_MERCH_TWEEN_MS / 1000}
        max={MAX_MERCH_TWEEN_MS / 1000}
        step={0.001}
        value={text}
        aria-label='Count duration in seconds'
        data-testid='merch-tween-duration-input'
        onChange={(e) => {
          const next = e.target.value
          const bad = e.target.validity.badInput
          setText(next)
          setBadInput(bad)
          if (bad) return
          const ms = parseMerchTweenSeconds(next)
          if (ms !== 'invalid') onCommit(ms)
        }}
        onBlur={(e) => {
          const formatted = formatMerchTweenSeconds(valueMs)
          setText(formatted)
          setBadInput(false)
          if (e.target.value !== formatted) e.target.value = formatted
        }}
        className={merchSelectClass}
      />
      {invalid && (
        <p className='mt-1 text-[11px] text-amber-300'>
          Must be {MIN_MERCH_TWEEN_MS / 1000} to {MAX_MERCH_TWEEN_MS / 1000} seconds — the
          previous duration is kept until this is fixed.
        </p>
      )}
      <p className='mt-1 text-[11px] text-dm-text-accent'>
        How long a changed number takes to roll from its old value to the
        new one.
      </p>
    </div>
  )
}

const MerchChangeAnimationPicker = ({
  value,
  tweenMs,
  onChange,
  onTweenMsChange
}: {
  value: MerchChangeAnimation
  tweenMs: number
  onChange: (next: MerchChangeAnimation) => void
  onTweenMsChange: (ms: number) => void
}): JSX.Element => (
  <div className='mt-4'>
    <FieldLabel>Change animation</FieldLabel>
    <div className='flex flex-col gap-1.5'>
      {MERCH_CHANGE_ANIMATION_OPTIONS.map((o) => (
        <label key={o.value} className='flex items-start gap-2 text-sm text-dm-text-primary'>
          <input
            type='radio'
            name='merch-change-animation'
            value={o.value}
            checked={value === o.value}
            onChange={() => onChange(o.value)}
            className='mt-1 accent-brand'
          />
          <span>
            {o.title}
            <span className='block text-[11px] text-dm-text-accent'>{o.copy}</span>
          </span>
        </label>
      ))}
    </div>
    {value === 'TWEEN' && (
      <MerchTweenDurationInput valueMs={tweenMs} onCommit={onTweenMsChange} />
    )}
  </div>
)

const MerchStyleSwitch = ({
  label,
  testId,
  checked,
  onLabel,
  offLabel,
  help,
  onChange
}: {
  label: string
  testId: string
  checked: boolean
  onLabel: string
  offLabel: string
  help: JSX.Element | string
  onChange: (v: boolean) => void
}): JSX.Element => (
  <div className='mt-4' data-testid={`${testId}-block`}>
    <FieldLabel>{label}</FieldLabel>
    <div className='flex items-center gap-3 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2'>
      <button
        type='button'
        role='switch'
        aria-checked={checked}
        aria-label={label}
        data-testid={`${testId}-switch`}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-brand ${checked ? 'bg-brand' : 'bg-white/15'}`}
      >
        <span
          aria-hidden='true'
          className={`inline-block size-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-[18px]' : 'translate-x-0.5'}`}
        />
      </button>
      <span className='text-sm text-dm-text-primary'>{checked ? onLabel : offLabel}</span>
    </div>
    <p className='mt-1 text-[11px] text-dm-text-accent'>{help}</p>
  </div>
)

const clampInt = (v: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, Math.round(v)))

const clamp2dp = (v: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, Math.round(v * 100) / 100))

const MerchTickerPxInput = ({
  label,
  value,
  min = 0,
  max,
  decimals = false,
  onChange
}: {
  label: string
  value: number
  min?: number
  max: number
  decimals?: boolean
  onChange: (next: number) => void
}): JSX.Element => (
  <label className='block'>
    <FieldLabel>{label}</FieldLabel>
    <input
      type='number'
      inputMode={decimals ? 'decimal' : 'numeric'}
      min={min}
      max={max}
      step={decimals ? 0.01 : 1}
      value={value}
      onChange={(e) => {
        const parsed = Number(e.target.value)
        if (!Number.isFinite(parsed)) return
        onChange(decimals ? clamp2dp(parsed, 0, max) : clampInt(parsed, 0, max))
      }}
      onBlur={() => {
        if (value < min) onChange(min)
      }}
      className={merchSelectClass}
    />
  </label>
)

const MerchTickerGapInput = ({
  label,
  value,
  onChange
}: {
  label: string
  value: number | null
  onChange: (next: number | null) => void
}): JSX.Element => (
  <label className='block'>
    <FieldLabel>{label}</FieldLabel>
    <input
      type='number'
      inputMode='decimal'
      min={0}
      max={MAX_TICKER_PAD_PX}
      step={0.01}
      placeholder='Auto'
      value={value ?? ''}
      onChange={(e) => {
        if (e.target.value === '') {
          onChange(null)
          return
        }
        const parsed = Number(e.target.value)
        if (!Number.isFinite(parsed)) return
        onChange(clamp2dp(parsed, 0, MAX_TICKER_PAD_PX))
      }}
      className={merchSelectClass}
    />
  </label>
)

const MerchSlotPriceInput = ({
  label,
  valueCents,
  onChange
}: {
  label: string
  valueCents: number | null
  onChange: (nextCents: number | null) => void
}): JSX.Element => (
  <label className='block'>
    <FieldLabel>{label}</FieldLabel>
    <input
      type='number'
      inputMode='decimal'
      min={0.01}
      max={MAX_TICKER_PRICE_SLOT_CENTS / 100}
      step={0.01}
      placeholder='Off'
      value={valueCents === null ? '' : valueCents / 100}
      onChange={(e) => {
        if (e.target.value === '') {
          onChange(null)
          return
        }
        const parsed = Number(e.target.value)
        if (!Number.isFinite(parsed)) return
        const cents = Math.round(parsed * 100)
        onChange(Math.min(MAX_TICKER_PRICE_SLOT_CENTS, Math.max(1, cents)))
      }}
      className={merchSelectClass}
    />
  </label>
)

const MerchSlotPctInput = ({
  label,
  value,
  onChange
}: {
  label: string
  value: number | null
  onChange: (next: number | null) => void
}): JSX.Element => (
  <label className='block'>
    <FieldLabel>{label}</FieldLabel>
    <input
      type='number'
      inputMode='decimal'
      min={0}
      max={MAX_TICKER_PCT_SLOT_PCT}
      step={0.1}
      placeholder='Off'
      value={value ?? ''}
      onChange={(e) => {
        if (e.target.value === '') {
          onChange(null)
          return
        }
        const parsed = Number(e.target.value)
        if (!Number.isFinite(parsed)) return
        onChange(Math.min(MAX_TICKER_PCT_SLOT_PCT, Math.max(0, Math.round(parsed * 10) / 10)))
      }}
      className={merchSelectClass}
    />
  </label>
)

const MerchPhotoHeightInput = ({
  label,
  value,
  onChange
}: {
  label: string
  value: number | null
  onChange: (next: number | null) => void
}): JSX.Element => (
  <label className='block'>
    <FieldLabel>{label}</FieldLabel>
    <input
      type='number'
      inputMode='numeric'
      min={MIN_MERCH_PHOTO_HEIGHT_PX}
      max={MAX_MERCH_PHOTO_HEIGHT_PX}
      step={1}
      placeholder='Auto'
      value={value ?? ''}
      onChange={(e) => {
        if (e.target.value === '') {
          onChange(null)
          return
        }
        const parsed = Number(e.target.value)
        if (!Number.isFinite(parsed)) return
        onChange(clampInt(parsed, 0, MAX_MERCH_PHOTO_HEIGHT_PX))
      }}
      onBlur={() => {
        if (value !== null && value < MIN_MERCH_PHOTO_HEIGHT_PX) onChange(MIN_MERCH_PHOTO_HEIGHT_PX)
      }}
      className={merchSelectClass}
    />
  </label>
)

const MerchTickerStyleSection = ({
  style,
  onChange
}: {
  style: MerchTickerStyleDraft
  onChange: (patch: Partial<MerchTickerStyleDraft>) => void
}): JSX.Element => {
  const solidPage = style.pageBackground !== 'transparent'
  const solidGap = style.gapColor !== 'transparent'
  return (
    <SectionCard title='Ticker style'>
      <div className='mb-4 grid grid-cols-1 gap-3 md:grid-cols-2'>
        <MerchFontSelect
          label='Font'
          value={style.fontKey}
          onChange={(v) => onChange({ fontKey: v })}
        />
        <ColorInput
          label='Text color'
          value={style.textColor}
          onChange={(v) => onChange({ textColor: v })}
          solidOnly
        />
        <ColorInput
          label='Top bar background'
          value={style.topBgColor}
          onChange={(v) => onChange({ topBgColor: v === 'transparent' ? '#00000000' : v })}
        />
        <ColorInput
          label='Bottom bar background'
          value={style.bottomBgColor}
          onChange={(v) => onChange({ bottomBgColor: v })}
          solidOnly
        />
      </div>
      <div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
        <label className='block'>
          <FieldLabel>Bottom bar opacity (%)</FieldLabel>
          <input
            type='number'
            inputMode='numeric'
            min={0}
            max={100}
            step={1}
            value={Math.round(style.bottomOpacity * 100)}
            onChange={(e) => {
              const parsed = Number(e.target.value)
              if (!Number.isFinite(parsed)) return
              onChange({ bottomOpacity: clampInt(parsed, 0, 100) / 100 })
            }}
            className={merchSelectClass}
          />
        </label>
        <MerchTickerPxInput
          label='Bottom bar top padding (px)'
          value={style.bottomPadTop}
          max={MAX_TICKER_PAD_PX}
          decimals
          onChange={(v) => onChange({ bottomPadTop: v })}
        />
        <MerchTickerPxInput
          label='Bottom bar bottom padding (px)'
          value={style.bottomPadBottom}
          max={MAX_TICKER_PAD_PX}
          decimals
          onChange={(v) => onChange({ bottomPadBottom: v })}
        />
        <MerchTickerPxInput
          label='Top bar top padding (px)'
          value={style.topPadTop}
          max={MAX_TICKER_PAD_PX}
          decimals
          onChange={(v) => onChange({ topPadTop: v })}
        />
        <MerchTickerPxInput
          label='Top bar bottom padding (px)'
          value={style.topPadBottom}
          max={MAX_TICKER_PAD_PX}
          decimals
          onChange={(v) => onChange({ topPadBottom: v })}
        />
        <MerchTickerPxInput
          label='Space above top bar'
          value={style.topOuterPad}
          max={MAX_TICKER_PAD_PX}
          decimals
          onChange={(v) => onChange({ topOuterPad: v })}
        />
        <MerchTickerPxInput
          label='Gap height (px)'
          value={style.gapHeight}
          max={MAX_TICKER_GAP_PX}
          onChange={(v) => onChange({ gapHeight: v })}
        />
        <MerchTickerPxInput
          label='Space below bottom bar'
          value={style.bottomOuterPad}
          max={MAX_TICKER_PAD_PX}
          decimals
          onChange={(v) => onChange({ bottomOuterPad: v })}
        />
        <MerchTickerGapInput
          label='Top bar separator gap (px)'
          value={style.topSeparatorGapPx}
          onChange={(v) => onChange({ topSeparatorGapPx: v })}
        />
        <MerchTickerGapInput
          label='Bottom bar separator gap (px)'
          value={style.bottomSeparatorGapPx}
          onChange={(v) => onChange({ bottomSeparatorGapPx: v })}
        />
        <MerchTickerPxInput
          label='Scroll speed (px/s)'
          value={style.scrollSpeedPxS}
          min={MIN_TICKER_SCROLL_SPEED_PX_S}
          max={MAX_TICKER_SCROLL_SPEED_PX_S}
          onChange={(v) => onChange({ scrollSpeedPxS: v })}
        />
        <MerchTickerPxInput
          label='Top bar text size (px)'
          value={style.topTextSizePx}
          min={MIN_TICKER_TEXT_SIZE_PX}
          max={MAX_TICKER_TEXT_SIZE_PX}
          onChange={(v) => onChange({ topTextSizePx: v })}
        />
        <MerchTickerPxInput
          label='Top bar line height (px)'
          value={style.topLineHeightPx}
          min={MIN_TICKER_LINE_HEIGHT_PX}
          max={MAX_TICKER_LINE_HEIGHT_PX}
          onChange={(v) => onChange({ topLineHeightPx: v })}
        />
        <MerchTickerPxInput
          label='Bottom bar text size (px)'
          value={style.bottomTextSizePx}
          min={MIN_TICKER_TEXT_SIZE_PX}
          max={MAX_TICKER_TEXT_SIZE_PX}
          onChange={(v) => onChange({ bottomTextSizePx: v })}
        />
        <MerchTickerPxInput
          label='Bottom bar line height (px)'
          value={style.bottomLineHeightPx}
          min={MIN_TICKER_LINE_HEIGHT_PX}
          max={MAX_TICKER_LINE_HEIGHT_PX}
          onChange={(v) => onChange({ bottomLineHeightPx: v })}
        />
        <MerchTickerPxInput
          label='Text glow (px, 0 = off)'
          value={style.textGlowPx}
          max={MAX_TICKER_TEXT_GLOW_PX}
          onChange={(v) => onChange({ textGlowPx: v })}
        />
        <MerchSlotPriceInput
          label='Expected price min ($)'
          valueCents={style.priceSlotMinCents}
          onChange={(v) => onChange({ priceSlotMinCents: v })}
        />
        <MerchSlotPriceInput
          label='Expected price max ($)'
          valueCents={style.priceSlotMaxCents}
          onChange={(v) => onChange({ priceSlotMaxCents: v })}
        />
        <MerchSlotPctInput
          label='Expected change max (±%)'
          value={style.pctSlotMaxPct}
          onChange={(v) => onChange({ pctSlotMaxPct: v })}
        />
      </div>
      <p className='mt-1 text-[11px] text-dm-text-accent'>
        Separator gap is the space on each side of every // divider in a
        bar (the bottom gap also spaces its entries apart). Leave a field
        empty for the automatic rhythm, 1.5× that bar&apos;s text size; a
        number is exact pixels. Top 25.2 and bottom 60 approximate the
        ticker&apos;s previous spacing.
      </p>
      <div className='mt-3 grid grid-cols-1 gap-3 md:grid-cols-2'>
        <ColorInput
          label='Bar border color'
          value={style.borderColor}
          onChange={(v) => onChange({ borderColor: v === 'transparent' ? '#00000000' : v })}
        />
        <MerchTickerPxInput
          label='Bar border width (px, 0 = off)'
          value={style.borderWidth}
          max={MAX_TICKER_BORDER_PX}
          onChange={(v) => onChange({ borderWidth: v })}
        />
      </div>
      <div className='mt-3 grid grid-cols-1 gap-3 md:grid-cols-2'>
        <div>
          <ColorInput
            label='Text glow color'
            value={style.textGlowColor}
            onChange={(v) => onChange({ textGlowColor: v === 'transparent' ? '#ffffff00' : v })}
          />
        </div>
      </div>
      <MerchStyleSwitch
        label='Glow on arrows'
        testId='ticker-text-glow-arrows'
        checked={style.textGlowArrows}
        onLabel='Arrows glow with the text'
        offLabel='Arrows stay crisp'
        help='Off removes the glow from the up/down arrows alone — prices, percentages and names keep it. Does nothing while the glow radius is 0.'
        onChange={(v) => onChange({ textGlowArrows: v })}
      />
      <div className='mt-4'>
        <FieldLabel>Gap color</FieldLabel>
        <div className='flex flex-col gap-1.5'>
          <label className='flex items-start gap-2 text-sm text-dm-text-primary'>
            <input
              type='radio'
              name='merch-ticker-gap-color'
              checked={!solidGap}
              onChange={() => onChange({ gapColor: 'transparent' })}
              className='mt-1 accent-brand'
            />
            <span>Transparent</span>
          </label>
          <label className='flex items-start gap-2 text-sm text-dm-text-primary'>
            <input
              type='radio'
              name='merch-ticker-gap-color'
              checked={solidGap}
              onChange={() => onChange({ gapColor: '#000000' })}
              className='mt-1 accent-brand'
            />
            <span>Solid color</span>
          </label>
        </div>
        {solidGap && (
          <div className='mt-2 md:w-1/2'>
            <ColorInput
              label='Gap fill'
              value={style.gapColor}
              onChange={(v) => onChange({ gapColor: v })}
            />
          </div>
        )}
      </div>
      <div className='mt-4'>
        <FieldLabel>Page background</FieldLabel>
        <div className='flex flex-col gap-1.5'>
          <label className='flex items-start gap-2 text-sm text-dm-text-primary'>
            <input
              type='radio'
              name='merch-ticker-page-bg'
              checked={!solidPage}
              onChange={() => onChange({ pageBackground: 'transparent' })}
              className='mt-1 accent-brand'
            />
            <span>Transparent</span>
          </label>
          <label className='flex items-start gap-2 text-sm text-dm-text-primary'>
            <input
              type='radio'
              name='merch-ticker-page-bg'
              checked={solidPage}
              onChange={() => onChange({ pageBackground: '#000000' })}
              className='mt-1 accent-brand'
            />
            <span>Solid color</span>
          </label>
        </div>
        {solidPage && (
          <div className='mt-2 md:w-1/2'>
            <ColorInput
              label='Page color'
              value={style.pageBackground}
              onChange={(v) => onChange({ pageBackground: v === 'transparent' ? '#000000' : v })}
              solidOnly
            />
          </div>
        )}
        <p className='mt-1 text-[11px] text-dm-text-accent'>
          Applies to the standalone overlay page only (the /o/merch browser
          source). Placed in a scene or composite overlay, the ticker always
          sits on a transparent page.
        </p>
      </div>
      <MerchChangeAnimationPicker
        value={style.changeAnimation}
        tweenMs={style.tweenMs}
        onChange={(v) => onChange({ changeAnimation: v })}
        onTweenMsChange={(ms) => onChange({ tweenMs: ms })}
      />

      <MerchStyleSwitch
        label='Bottom bar direction'
        testId='ticker-bottom-scroll-reverse'
        checked={style.bottomScrollReverse}
        onLabel='Reversed — the bottom bar scrolls the other way'
        offLabel='Same as the top bar'
        help='Both bars always move at the speed set above; this only flips which way the message bar travels.'
        onChange={(v) => onChange({ bottomScrollReverse: v })}
      />
    </SectionCard>
  )
}

const TICKER_LABEL_COLOR_OPTIONS: ReadonlyArray<{
  value: MerchTickerLabelColor
  label: string
  title: string
  on: string
  off: string
}> = [
  {
    value: 'green',
    label: 'Green',
    title: 'Green label',
    on: 'bg-emerald-500/25 text-emerald-300 ring-emerald-500/50',
    off: 'text-dm-text-accent ring-white/10 hover:text-emerald-300'
  },
  {
    value: 'red',
    label: 'Red',
    title: 'Red label',
    on: 'bg-red-500/25 text-red-300 ring-red-500/50',
    off: 'text-dm-text-accent ring-white/10 hover:text-red-300'
  },
  {
    value: 'white',
    label: 'White',
    title: "White label — the marquee's own text colour",
    on: 'bg-white/20 text-white ring-white/50',
    off: 'text-dm-text-accent ring-white/10 hover:text-white'
  },
  {
    value: 'hide',
    label: 'Hide',
    title: 'No label at all — the message is just its text',
    on: 'bg-dm-bg-accent text-dm-text-primary ring-white/30',
    off: 'text-dm-text-accent ring-white/10 hover:text-dm-text-primary'
  }
]

const MerchTickerMessagesSection = ({
  messages,
  onChange
}: {
  messages: MerchTickerMessageDraft[]
  onChange: (next: MerchTickerMessageDraft[]) => void
}): JSX.Element => {
  const atCap = messages.length >= MAX_TICKER_MESSAGES
  const inputClass = 'w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary placeholder:text-dm-text-accent focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
  const patchRow = (key: string, patch: Partial<MerchTickerMessageDraft>): void => {
    onChange(messages.map((m) => m.key === key ? { ...m, ...patch } : m))
  }
  return (
    <SectionCard
      title={`Messages (${messages.length}/${MAX_TICKER_MESSAGES})`}
      action={
        <button
          type='button'
          data-testid='add-ticker-message-button'
          onClick={() => onChange([...messages, emptyTickerMessage()])}
          disabled={atCap}
          title={atCap ? `The rotation is at its ${MAX_TICKER_MESSAGES}-message cap` : 'Add a message'}
          className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
        >
          <PlusIcon className='size-3.5' aria-hidden='true' />
          Add message
        </button>
      }
    >
      {messages.length === 0 && (
        <p className='rounded-lg border border-dashed border-white/10 px-4 py-6 text-center text-sm text-dm-text-secondary'>
          No messages yet, the bottom bar stays empty (its background still
          paints). Each message scrolls as LABEL // text.
        </p>
      )}
      {messages.length > 0 && (
        <ul className='space-y-1.5' data-testid='ticker-messages-list'>
          {messages.map((m) => (
            <li key={m.key} className='rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2'>
              <div className='flex flex-col gap-2 md:flex-row md:items-center'>
                <input
                  value={m.label}
                  onChange={(e) => patchRow(m.key, { label: e.target.value })}
                  placeholder={m.labelColor === 'hide' ? 'LABEL (hidden)' : 'LABEL'}
                  maxLength={MAX_TICKER_LABEL_LEN}
                  className={m.labelColor === 'hide'
                    ? `${inputClass} md:w-44 text-dm-text-accent line-through decoration-white/30`
                    : `${inputClass} md:w-44`}
                />

                <div className='flex shrink-0 items-center gap-1' data-testid={`ticker-label-color-${m.key}`}>
                  {TICKER_LABEL_COLOR_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type='button'
                      onClick={() => patchRow(m.key, { labelColor: opt.value })}
                      title={opt.title}
                      aria-pressed={m.labelColor === opt.value}
                      className={m.labelColor === opt.value
                        ? `rounded-md px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${opt.on}`
                        : `rounded-md px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${opt.off}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <input
                  value={m.text}
                  onChange={(e) => patchRow(m.key, { text: e.target.value })}
                  placeholder='Message text'
                  maxLength={MAX_TICKER_TEXT_LEN}
                  className={`${inputClass} md:flex-1`}
                />
                <button
                  type='button'
                  onClick={() => onChange(messages.filter((x) => x.key !== m.key))}
                  title='Remove message'
                  className='shrink-0 self-start rounded-md p-1.5 text-dm-text-accent hover:bg-red-500/10 hover:text-red-300 md:self-auto'
                >
                  <TrashIcon className='size-4' aria-hidden='true' />
                  <span className='sr-only'>Remove message</span>
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <p className='mt-2 text-[11px] text-dm-text-accent'>
        This order is the rotation order on stream. Labels cap at{' '}
        {MAX_TICKER_LABEL_LEN} characters, text at {MAX_TICKER_TEXT_LEN};
        saving publishes the new rotation to live sources immediately.
        Hide drops the heading and its {' // '} divider, so the message
        scrolls as text alone the label you typed is kept and comes
        back if you pick a colour again.
      </p>
    </SectionCard>
  )
}

const MerchExtensionVisibleSwitch = ({
  checked,
  onChange
}: {
  checked: boolean
  onChange: (v: boolean) => void
}): JSX.Element => (
  <div className='mt-4' data-testid='merch-extension-visible-block'>
    <FieldLabel>Show on Twitch</FieldLabel>
    <div className='flex items-center gap-3 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2'>
      <button
        type='button'
        role='switch'
        aria-checked={checked}
        aria-label='Show on Twitch'
        data-testid='merch-extension-visible-switch'
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-brand ${checked ? 'bg-brand' : 'bg-white/15'}`}
      >
        <span
          aria-hidden='true'
          className={`inline-block size-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-[18px]' : 'translate-x-0.5'}`}
        />
      </button>
      <span className='text-sm text-dm-text-primary'>
        {checked ? 'Shown to viewers' : 'Hidden from viewers'}
      </span>
    </div>
    <p className='mt-1 text-[11px] text-dm-text-accent'>
      Off hides the merch extension for every viewer within seconds; on
      brings it back. The extension stays installed on your channel until
      you uninstall it in Twitch.
    </p>
  </div>
)

const MerchExtensionDelayInput = ({
  valueMs,
  onCommit
}: {
  valueMs: number
  onCommit: (ms: number) => void
}): JSX.Element => {
  const [text, setText] = useState(formatExtensionDelaySeconds(valueMs))
  const [badInput, setBadInput] = useState(false)
  const invalid = badInput || parseExtensionDelaySeconds(text) === 'invalid'
  return (
    <div className='mt-4' data-testid='merch-extension-delay-block'>
      <FieldLabel>Twitch extension delay (seconds)</FieldLabel>
      <input
        type='number'
        inputMode='decimal'
        min={0}
        max={MAX_EXTENSION_DELAY_MS / 1000}
        step={0.001}
        value={text}
        aria-label='Twitch extension delay in seconds'
        data-testid='merch-extension-delay-input'
        onChange={(e) => {
          const next = e.target.value
          const bad = e.target.validity.badInput
          setText(next)
          setBadInput(bad)
          if (bad) return
          const ms = parseExtensionDelaySeconds(next)
          if (ms !== 'invalid') onCommit(ms)
        }}
        onBlur={(e) => {
          const formatted = formatExtensionDelaySeconds(valueMs)
          setText(formatted)
          setBadInput(false)
          if (e.target.value !== formatted) e.target.value = formatted
        }}
        className={merchSelectClass}
      />
      {invalid && (
        <p className='mt-1 text-[11px] text-amber-300'>
          Must be 0 to 60 seconds. The previous delay is kept until this is fixed.
        </p>
      )}
      <p className='mt-1 text-[11px] text-dm-text-accent'>
        Delays every update reaching the Twitch extension by this much so
        it lines up with your stream&apos;s delay. Twitch extension only; OBS
        overlays are unaffected.
      </p>
    </div>
  )
}

const MerchAutoReorderSwitch = ({
  checked,
  onChange
}: {
  checked: boolean
  onChange: (v: boolean) => void
}): JSX.Element => (
  <div className='mt-4' data-testid='merch-auto-reorder-block'>
    <FieldLabel>Reorder items on change</FieldLabel>
    <div className='flex items-center gap-3 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2'>
      <button
        type='button'
        role='switch'
        aria-checked={checked}
        aria-label='Reorder items on change'
        data-testid='merch-auto-reorder-switch'
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-brand ${checked ? 'bg-brand' : 'bg-white/15'}`}
      >
        <span
          aria-hidden='true'
          className={`inline-block size-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-[18px]' : 'translate-x-0.5'}`}
        />
      </button>
      <span className='text-sm text-dm-text-primary'>
        {checked ? 'Items reorder live' : 'Items keep your order'}
      </span>
    </div>
    <p className='mt-1 text-[11px] text-dm-text-accent'>
      Off keeps items in your arranged order: prices, arrows and flashes
      still update live. Your arrangement and the reorder arrows stay
      authoritative; turning this back on resumes the live standings.
    </p>
  </div>
)

const MerchExtensionStartCollapsedSwitch = ({
  checked,
  onChange
}: {
  checked: boolean
  onChange: (v: boolean) => void
}): JSX.Element => (
  <div className='mt-4' data-testid='merch-extension-start-collapsed-block'>
    <FieldLabel>New viewers first see</FieldLabel>
    <div className='flex items-center gap-3 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2'>
      <button
        type='button'
        role='switch'
        aria-checked={checked}
        aria-label='New viewers first see the collapsed MERCH tab'
        data-testid='merch-extension-start-collapsed-switch'
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-brand ${checked ? 'bg-brand' : 'bg-white/15'}`}
      >
        <span
          aria-hidden='true'
          className={`inline-block size-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-[18px]' : 'translate-x-0.5'}`}
        />
      </button>
      <span className='text-sm text-dm-text-primary'>
        {checked ? 'MERCH tab (collapsed)' : 'Full board'}
      </span>
    </div>
    <p className='mt-1 text-[11px] text-dm-text-accent'>
      Applies only to viewers who have never opened or closed the board
      themselves, anyone who has keeps their own choice.
    </p>
  </div>
)

const MerchExtensionStartWidthInput = ({
  value,
  onChange
}: {
  value: number | null
  onChange: (next: number | null) => void
}): JSX.Element => (
  <div className='mt-4' data-testid='merch-extension-start-width-block'>
    <FieldLabel>Starting size (% of player width)</FieldLabel>
    <input
      type='number'
      inputMode='decimal'
      min={MIN_EXTENSION_START_WIDTH_PCT}
      max={MAX_EXTENSION_START_WIDTH_PCT}
      step={0.01}
      placeholder='Auto'
      value={value ?? ''}
      aria-label='Starting size as a percent of the player width'
      data-testid='merch-extension-start-width-input'
      onChange={(e) => {
        if (e.target.value === '') {
          onChange(null)
          return
        }
        const parsed = Number(e.target.value)
        if (!Number.isFinite(parsed)) return
        onChange(clamp2dp(parsed, 0, MAX_EXTENSION_START_WIDTH_PCT))
      }}
      onBlur={() => {
        if (value !== null && value < MIN_EXTENSION_START_WIDTH_PCT) {
          onChange(MIN_EXTENSION_START_WIDTH_PCT)
        }
      }}
      className={merchSelectClass}
    />
    <p className='mt-1 text-[11px] text-dm-text-accent'>
      How wide the board first appears in the Twitch extension, as a
      percent of the player ({MIN_EXTENSION_START_WIDTH_PCT}–{MAX_EXTENSION_START_WIDTH_PCT}).
      Approximate: the extension snaps to its own size steps. Empty =
      Auto, today&apos;s size. Viewers who have resized the board keep
      their own size.
    </p>
  </div>
)

const MerchExtensionChannelInput = ({
  value,
  resolved,
  onChange
}: {
  value: string
  resolved: { login: string, channelId: string } | null
  onChange: (v: string) => void
}): JSX.Element => {
  const typed = value.trim().toLowerCase()
  const savedLogin = resolved?.login ?? ''
  const unsaved = typed !== savedLogin
  return (
    <div className='mt-4' data-testid='merch-extension-channel-block'>
      <FieldLabel>Twitch channel for this board</FieldLabel>
      <input
        type='text'
        value={value}
        autoComplete='off'
        spellCheck={false}
        placeholder='channel name (leave empty for the default)'
        aria-label='Twitch channel for this board'
        data-testid='merch-extension-channel-input'
        onChange={(e) => onChange(e.target.value)}
        className={merchSelectClass}
      />
      {savedLogin !== '' && (
        <p className='mt-1 text-[11px] text-dm-text-primary' data-testid='merch-extension-channel-resolved'>
          Publishing to <span className='font-mono'>{savedLogin}</span>
          {' '}(channel id <span className='font-mono'>{resolved?.channelId ?? ''}</span>).
          {unsaved && ' Save to change it.'}
        </p>
      )}
      {savedLogin === '' && unsaved && (
        <p className='mt-1 text-[11px] text-amber-300' data-testid='merch-extension-channel-unsaved'>
          Not saved yet: Save checks this channel name with Twitch and
          shows the channel id it resolved to.
        </p>
      )}
      <p className='mt-1 text-[11px] text-dm-text-accent'>
        The Twitch channel that receives live extension updates for this
        board. Leave it empty to use the event feed adapter&apos;s
        configured channel. Type the channel name, the
        name is checked with Twitch when you save. The extension must
        also be installed and configured with this board&apos;s id on
        that channel, which is a separate step in the Twitch dashboard.
      </p>
    </div>
  )
}

const MerchExtensionQuickToggle = ({
  overlay,
  busy,
  onToggle
}: {
  overlay: MerchOverlaySummary
  busy: boolean
  onToggle: () => void
}): JSX.Element => {
  const visible = merchExtensionVisibleOf(overlay)
  const Icon = visible ? EyeSlashIcon : EyeIcon
  return (
    <button
      type='button'
      onClick={onToggle}
      disabled={busy}
      title={visible ? 'Hide the Twitch extension for every viewer' : 'Show the Twitch extension to every viewer'}
      data-testid='merch-extension-quick-toggle'
      className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
    >
      {busy ? <LoadingSpinner /> : <Icon className='size-3.5' aria-hidden='true' />}
      {visible ? 'Hide on Twitch' : 'Show on Twitch'}
    </button>
  )
}

const MerchOverlayForm = ({
  existing,
  displayStyle,
  auth0Token,
  overlayLink,
  onSaved,
  onChanged,
  onCancel,
  backLabel,
  superuser
}: {
  existing: MerchOverlaySummary | null
  displayStyle: MerchDisplayStyle
  auth0Token: string | undefined
  overlayLink: AccountOverlayLinkState
  onSaved: (overlay: MerchOverlaySummary) => void
  onChanged?: (overlay: MerchOverlaySummary) => void
  onCancel: () => void
  backLabel: string
  superuser: boolean
}): JSX.Element => {
  const isEdit = existing !== null
  const isTicker = displayStyle === 'TICKER'
  const [draft, setDraft] = useState<MerchOverlayDraft>(
    existing === null ? emptyDraft() : draftFromOverlay(existing)
  )
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedNote, setSavedNote] = useState(false)
  useEffect(() => {
    setSavedNote(false)
  }, [draft])
  const [pickerOpen, setPickerOpen] = useState(false)

  const [shops, setShops] = useState<ShopifyShop[] | null>(null)
  const [shopsError, setShopsError] = useState<string | null>(null)

  useEffect(() => {
    if (auth0Token === undefined) return
    let cancelled = false
    graphqlRequest<{ shops: ShopifyShop[] }>(listMyShopifyShopsQuery, {}, auth0Token)
      .then(({ shops: fetched }) => {
        if (!cancelled) setShops(fetched)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setShopsError(err instanceof Error ? err.message : 'Failed to load your shops')
        setShops([])
      })
    return () => { cancelled = true }
  }, [auth0Token])

  const existingIds = useMemo(
    () => new Set(draft.items.map((i) => i.productId)),
    [draft.items]
  )
  const atItemCap = draft.items.length >= MAX_MERCH_ITEMS

  const addItem = (item: MerchItem): void => {
    setDraft((d) => {
      if (d.items.length >= MAX_MERCH_ITEMS) return d
      if (d.items.some((i) => i.productId === item.productId)) return d
      const basePriceCents = item.basePriceCents ??
        (item.priceCents !== null && item.priceCents > 0 ? item.priceCents : null)
      return { ...d, items: [...d.items, { ...item, basePriceCents }] }
    })
  }

  const moveItem = (index: number, dir: -1 | 1): void => {
    setDraft((d) => {
      const target = index + dir
      if (target < 0 || target >= d.items.length) return d
      const items = [...d.items]
      const tmp = items[index]
      items[index] = items[target]
      items[target] = tmp
      return { ...d, items }
    })
  }

  const [moveBusyId, setMoveBusyId] = useState<string | null>(null)
  const moveSavedItem = async (productId: string, direction: MerchMoveDirection): Promise<void> => {
    if (existing === null || auth0Token === undefined || moveBusyId !== null) return
    setMoveBusyId(productId)
    setError(null)
    try {
      const { result } = await graphqlRequest<{ result: MerchMoveResult }>(
        moveMerchBoardItemMutation,
        { merchOverlayId: existing.merchOverlayId, productId, direction },
        auth0Token
      )
      setDraft((d) => ({ ...d, items: sortItemsByLadder(d.items, result.order) }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to move the item')
    } finally {
      setMoveBusyId(null)
    }
  }

  const removeItem = (productId: string): void => {
    setDraft((d) => ({ ...d, items: d.items.filter((i) => i.productId !== productId) }))
  }

  const setBasePrice = (productId: string, basePriceCents: number | null): void => {
    setDraft((d) => ({
      ...d,
      items: d.items.map((i) => i.productId === productId ? { ...i, basePriceCents } : i)
    }))
  }

  const setLink = (productId: string, linkUrl: string | null): void => {
    setDraft((d) => ({
      ...d,
      items: d.items.map((i) => i.productId === productId ? { ...i, linkUrl } : i)
    }))
  }

  const setVariantOptions = (productId: string, variantOptions: MerchVariantOption[]): void => {
    setDraft((d) => ({
      ...d,
      items: d.items.map((i) => i.productId === productId ? { ...i, variantOptions } : i)
    }))
  }

  const setTitle = (productId: string, title: string): void => {
    setDraft((d) => ({
      ...d,
      items: d.items.map((i) => i.productId === productId ? { ...i, title } : i)
    }))
  }

  const setFeaturedImage = (productId: string, url: string): void => {
    setDraft((d) => ({
      ...d,
      items: d.items.map((i) => i.productId === productId ? { ...i, imageUrl: url } : i)
    }))
  }

  const addItemPhoto = (productId: string, url: string): void => {
    setDraft((d) => ({
      ...d,
      items: d.items.map((i) => i.productId === productId ? appendItemPhoto(i, url) : i)
    }))
  }

  const removeItemPhotoFromDraft = (productId: string, url: string): void => {
    setDraft((d) => ({
      ...d,
      items: d.items.map((i) => i.productId === productId ? removeItemPhoto(i, url) : i)
    }))
  }

  const [photoUploadsInFlight, setPhotoUploadsInFlight] = useState(0)
  const photoUploading = photoUploadsInFlight > 0
  const onPhotoUploadingChange = (uploading: boolean): void => {
    setPhotoUploadsInFlight((n) => Math.max(0, n + (uploading ? 1 : -1)))
  }

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (auth0Token === undefined || submitting || photoUploading) return
    setSubmitting(true)
    setError(null)
    try {
      const variables = {
        name: draft.name.trim(),
        storeTitle: draft.storeTitle.trim(),
        shopDomain: draft.shopDomain === '' ? null : draft.shopDomain,
        items: draft.items.map(({ productId, title, priceCents, basePriceCents, imageUrl, imageUrls, linkUrl, defaultVariantId, variantOptionName, variantOptions }) => ({
          productId,
          title,
          priceCents,
          basePriceCents: basePriceCents ?? null,
          imageUrl: isTicker ? null : imageUrl,
          imageUrls: isTicker ? null : (imageUrls ?? null),
          linkUrl,
          defaultVariantId: defaultVariantId ?? null,
          variantOptionName: variantOptionName ?? null,
          variantOptions: isTicker
            ? []
            : (variantOptions ?? []).map((v) => ({ id: v.id, label: v.label, linkUrl: v.linkUrl ?? null }))
        })),
        ...(isTicker
          ? {}
          : {
              extensionVisible: draft.extensionVisible,
              extensionDelayMs: draft.extensionDelayMs,
              autoReorder: draft.autoReorder,
              extensionStartCollapsed: draft.extensionStartCollapsed,
              extensionStartWidthPct: draft.extensionStartWidthPct,
              extensionChannelLogin: draft.extensionChannelLogin.trim() === ''
                ? null
                : draft.extensionChannelLogin.trim().toLowerCase(),
              shopMode: draft.shopMode,
              shopLabelText: draft.shopLabelText,
              ctaText: draft.ctaText,
              style: {
                titleColor: draft.titleColor,
                titleFontKey: draft.titleFontKey,
                productTitleColor: draft.productTitleColor,
                productTitleFontKey: draft.productTitleFontKey,
                priceFontKey: draft.priceFontKey,
                boardBgColor: draft.boardBgColor,
                rowBgColor: draft.rowBgColor,
                tileBgColor: draft.tileBgColor,
                outerBgColor: draft.outerBgColor,
                outerCornerRadius: draft.outerCornerRadius,
                boardCornerRadius: draft.boardCornerRadius,
                outerBorderColor: draft.outerBorderColor,
                outerBorderWidth: draft.outerBorderWidth,
                tabOutlineColor: draft.tabOutlineColor,
                tabTextColor: draft.tabTextColor,
                changeAnimation: draft.changeAnimation,
                tweenMs: draft.tweenMs,
                showVariants: draft.showVariants,
                squarePhotos: draft.squarePhotos,
                offsiteMarkColor: draft.offsiteMarkColor === '' ? null : draft.offsiteMarkColor,
                textGlowPx: draft.textGlowPx,
                textGlowColor: draft.textGlowColor,
                textGlowArrows: draft.textGlowArrows,
                outerPadX: draft.outerPadX,
                boardPadX: draft.boardPadX,
                shopLabelColor: draft.shopLabelColor,
                ctaColor: draft.ctaColor,
                outerPadTop: draft.outerPadTop,
                outerPadBottom: draft.outerPadBottom,
                boardBgImage: draft.boardBgImage.trim() === '' ? null : draft.boardBgImage.trim(),
                productImageHeight: draft.productImageHeight,
                rowBorderColor: draft.rowBorderColor,
                collapseXSize: draft.collapseXSize,
                collapseXColor: draft.collapseXColor === '' ? null : draft.collapseXColor
              }
            })
      }
      const tickerVariables = isTicker
        ? {
            messages: draft.messages.map(({ label, labelColor, text }) => ({
              label: label.trim(),
              labelColor,
              text: text.trim()
            })).filter((m) => m.label !== '' || m.text !== ''),
            tickerStyle: { ...draft.tickerStyle }
          }
        : {}
      const createVariables = isTicker
        ? { displayStyle: 'TICKER', mode: 'PRICES' }
        : { mode: draft.mode }
      const { overlay } = existing === null
        ? await graphqlRequest<{ overlay: MerchOverlaySummary }>(
          createMerchOverlayMutation, { ...variables, ...tickerVariables, ...createVariables }, auth0Token
        )
        : isTicker
          ? await graphqlRequest<{ overlay: MerchOverlaySummary }>(
            updateMerchTickerMutation, { merchOverlayId: existing.merchOverlayId, ...variables, ...tickerVariables }, auth0Token
          )
          : await graphqlRequest<{ overlay: MerchOverlaySummary }>(
            updateMerchOverlayMutation, { merchOverlayId: existing.merchOverlayId, ...variables }, auth0Token
          )
      setSubmitting(false)
      setSavedNote(true)
      onSaved(overlay)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save merch board')
      setSubmitting(false)
    }
  }

  const draftValid = draft.name.trim() !== '' && draft.storeTitle.trim() !== ''
  const inputClass = 'w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
  const styleNoun = isTicker ? 'merch ticker' : 'merch board'
  const formTitle = isEdit ? `Edit ${styleNoun}` : `New ${styleNoun}`
  const shopBound = draft.shopDomain !== ''
  const priceMode = draft.mode === 'PRICES' || isTicker
  const variantCartBase = shopBound ? `https://${draft.shopDomain}/cart/` : 'https://<store>/cart/'

  let itemOrderHint: string
  if (isTicker) {
    itemOrderHint = 'The top bar scrolls these products with live prices on stream they order by % change against each item’s base price, server-side. The order here is only this editing list.'
  } else if (priceMode) {
    itemOrderHint = 'Price-based board: on stream the rows sort by % change against each item’s base price live, server-side. The order here is only this editing list.'
  } else if (isEdit) {
    itemOrderHint = 'The arrows move the item on the LIVE board one position per press, published immediately (newly added items join the bottom of the ladder at save).'
  } else {
    itemOrderHint = 'This order is the board’s starting ladder: sales move an item up by the quantity bought, and it holds its position between sales.'
  }

  return (
    <form
      onSubmit={(e) => { void handleSubmit(e) }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
          e.preventDefault()
        }
      }}
      className='mt-2 grid grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-3'
    >
      <div className='space-y-1.5 lg:col-span-2'>
        <fieldset className='contents space-y-1.5'>
          <SectionCard
            title={formTitle}
            action={
              <button
                type='button'
                onClick={onCancel}
                className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
              >
                <ArrowLeftIcon className='size-3.5' aria-hidden='true' />
                {backLabel}
              </button>
            }
          >
            <label className='mb-4 block'>
              <FieldLabel>Name</FieldLabel>
              <input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder='Main merch board, Show-day ticker, …'
                required
                maxLength={MAX_TITLE_LEN}
                className={inputClass}
              />
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                An identifier, not displayed on the overlay.
              </p>
            </label>

            <label className='mb-4 block'>
              <FieldLabel>Store title</FieldLabel>
              <input
                value={draft.storeTitle}
                onChange={(e) => setDraft((d) => ({ ...d, storeTitle: e.target.value }))}
                placeholder='THE MERCH EXCHANGE'
                required
                maxLength={MAX_TITLE_LEN}
                className={inputClass}
              />
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                {isTicker
                  ? 'Kept with the config, the ticker strip itself renders no heading.'
                  : 'The heading rendered above the board on stream.'}
              </p>
            </label>

            {isTicker && (
              <div className='mb-4'>
                <FieldLabel>Board mode</FieldLabel>
                <p className='rounded-md border border-white/5 bg-dm-bg-accent/20 px-3 py-2 text-[11px] leading-relaxed text-dm-text-secondary'>
                  Merch tickers are always price-based: the bound store&apos;s
                  price changes drive the scrolling quotes. Orders never
                  move a ticker.
                </p>
              </div>
            )}

            {!isTicker && (
              <div className='mb-4'>
                <FieldLabel>Board mode</FieldLabel>
                <div className='flex flex-col gap-1.5'>
                  <label className='flex items-start gap-2 text-sm text-dm-text-primary'>
                    <input
                      type='radio'
                      name='merch-board-mode'
                      value='PRICES'
                      checked={draft.mode === 'PRICES'}
                      disabled={isEdit}
                      onChange={() => setDraft((d) => ({ ...d, mode: 'PRICES' }))}
                      className='mt-1 accent-brand disabled:cursor-not-allowed'
                    />
                    <span>
                      Price-based
                      <span className='block text-[11px] text-dm-text-accent'>
                        A stock market: the store&apos;s price changes move the
                        rows, sorted by % change against each item&apos;s base
                        price.
                      </span>
                    </span>
                  </label>
                  <label className='flex items-start gap-2 text-sm text-dm-text-primary'>
                    <input
                      type='radio'
                      name='merch-board-mode'
                      value='ORDERS'
                      checked={draft.mode === 'ORDERS'}
                      disabled={isEdit}
                      onChange={() => setDraft((d) => ({ ...d, mode: 'ORDERS' }))}
                      className='mt-1 accent-brand disabled:cursor-not-allowed'
                    />
                    <span>
                      Order-based
                      <span className='block text-[11px] text-dm-text-accent'>
                        The sales ladder: paid orders move items up, and they
                        hold their positions between sales.
                      </span>
                    </span>
                  </label>
                </div>
                {isEdit && (
                  <p className='mt-1 text-[11px] text-amber-300'>
                    Mode is fixed at creation: create a new board to switch.
                  </p>
                )}
              </div>
            )}

            <div>
              <FieldLabel>Shopify store</FieldLabel>
              {shopsError !== null && (
                <div className='mb-2'>
                  <ErrorInline onDismiss={() => setShopsError(null)}>{shopsError}</ErrorInline>
                </div>
              )}
              {shops === null && (
                <div className='h-9 animate-pulse rounded-md bg-white/5' aria-hidden='true' />
              )}
              {shops !== null && shops.length === 0 && (
                <p className='rounded-md border border-white/5 bg-dm-bg-accent/20 px-3 py-2 text-[11px] leading-relaxed text-dm-text-secondary'>
                  No Shopify store is registered for your account yet.
                  {superuser && (
                    <>
                      {' '}Register one in the{' '}
                      <Link to={ADMIN_PATH} className='font-medium text-brand underline hover:text-brand/80'>
                        Admin merch registry
                      </Link>.
                    </>
                  )}
                  {!superuser && ' Shops are registered by an operator, ask your admin.'}
                  {' '}You can still build the board; it renders config-only until a store feeds it.
                </p>
              )}
              {shops !== null && shops.length > 0 && (
                <select
                  value={draft.shopDomain}
                  onChange={(e) => setDraft((d) => ({ ...d, shopDomain: e.target.value }))}
                  className={inputClass}
                >
                  <option value=''>No store bound (config-only board)</option>
                  {shops.map((s) => (
                    <option key={s.shop} value={s.shop}>{s.shop}</option>
                  ))}
                </select>
              )}
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                {priceMode
                  ? 'The bound store’s price changes move this board live. One store per board.'
                  : 'The bound store’s paid orders and refunds move this board live. One store per board.'}
              </p>
            </div>
            {!isTicker && (
              <>
                <MerchExtensionVisibleSwitch
                  checked={draft.extensionVisible}
                  onChange={(v) => setDraft((d) => ({ ...d, extensionVisible: v }))}
                />
                <MerchExtensionDelayInput
                  valueMs={draft.extensionDelayMs}
                  onCommit={(ms) => setDraft((d) => ({ ...d, extensionDelayMs: ms }))}
                />
                <MerchAutoReorderSwitch
                  checked={draft.autoReorder}
                  onChange={(v) => setDraft((d) => ({ ...d, autoReorder: v }))}
                />
                <MerchExtensionStartCollapsedSwitch
                  checked={draft.extensionStartCollapsed}
                  onChange={(v) => setDraft((d) => ({ ...d, extensionStartCollapsed: v }))}
                />
                <MerchExtensionStartWidthInput
                  value={draft.extensionStartWidthPct}
                  onChange={(v) => setDraft((d) => ({ ...d, extensionStartWidthPct: v }))}
                />
                <MerchExtensionChannelInput
                  value={draft.extensionChannelLogin}
                  resolved={
                    existing !== null && merchExtensionChannelLoginOf(existing) !== ''
                      ? {
                          login: merchExtensionChannelLoginOf(existing),
                          channelId: merchExtensionChannelIdOf(existing)
                        }
                      : null
                  }
                  onChange={(v) => setDraft((d) => ({ ...d, extensionChannelLogin: v }))}
                />
              </>
            )}
          </SectionCard>

          <SectionCard
            title={`Items (${draft.items.length}/${MAX_MERCH_ITEMS})`}
            action={
              <button
                type='button'
                data-testid='add-from-store-button'
                onClick={() => setPickerOpen(true)}
                disabled={!shopBound || atItemCap || auth0Token === undefined}
                title={shopBound ? 'Pick products from the bound Shopify store' : 'Bind a Shopify store first'}
                className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
              >
                <BuildingStorefrontIcon className='size-3.5' aria-hidden='true' />
                Add from your store
              </button>
            }
          >
            {draft.items.length === 0 && (
              <p className='rounded-lg border border-dashed border-white/10 px-4 py-6 text-center text-sm text-dm-text-secondary'>
                No items yet, pick products from your store or add one manually below.
              </p>
            )}
            {draft.items.length > 0 && (
              <ul className='space-y-1.5' data-testid='merch-items-list'>
                {draft.items.map((item, index) => (
                  <li
                    key={item.productId}
                    className='rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2'
                  >
                    <div className='flex items-center gap-3'>
                      {!isTicker && (
                        <div className='flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-white/10 bg-white' data-testid={`merch-item-thumb-${item.productId}`}>
                          {item.imageUrl !== null
                            ? <img src={item.imageUrl} alt='' loading='lazy' className='size-full object-contain' />
                            : <span className='text-[10px] font-bold text-zinc-900'>{item.title.slice(0, 2).toUpperCase()}</span>}
                        </div>
                      )}
                      <div className='flex min-w-0 flex-1 flex-col leading-tight'>
                        <span className='truncate text-sm font-medium text-dm-text-primary'>{item.title}</span>
                        <span className='truncate text-[11px] text-dm-text-accent'>
                          {priceLabel(item.priceCents)} · id {item.productId}
                          {item.linkUrl !== null && ' · linked'}
                        </span>
                      </div>
                      <div className='flex shrink-0 items-center gap-0.5'>
                        {!priceMode && (
                          <>
                            <button
                              type='button'
                              onClick={() => {
                                if (isEdit) void moveSavedItem(item.productId, 'UP')
                                else moveItem(index, -1)
                              }}
                              disabled={index === 0 || moveBusyId !== null}
                              title={isEdit ? 'Move up one ladder position (publishes live)' : 'Move up'}
                              className='rounded-md p-1.5 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-40'
                            >
                              {moveBusyId === item.productId ? <LoadingSpinner /> : <ChevronUpIcon className='size-4' aria-hidden='true' />}
                              <span className='sr-only'>Move {item.title} up</span>
                            </button>
                            <button
                              type='button'
                              onClick={() => {
                                if (isEdit) void moveSavedItem(item.productId, 'DOWN')
                                else moveItem(index, 1)
                              }}
                              disabled={index === draft.items.length - 1 || moveBusyId !== null}
                              title={isEdit ? 'Move down one ladder position (publishes live)' : 'Move down'}
                              className='rounded-md p-1.5 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-40'
                            >
                              <ChevronDownIcon className='size-4' aria-hidden='true' />
                              <span className='sr-only'>Move {item.title} down</span>
                            </button>
                          </>
                        )}
                        <button
                          type='button'
                          onClick={() => removeItem(item.productId)}
                          title='Remove'
                          className='rounded-md p-1.5 text-dm-text-accent hover:bg-red-500/10 hover:text-red-300'
                        >
                          <TrashIcon className='size-4' aria-hidden='true' />
                          <span className='sr-only'>Remove {item.title}</span>
                        </button>
                      </div>
                    </div>
                    <div className='mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-white/5 pt-2'>
                      <MerchItemTitleInput
                        productId={item.productId}
                        value={item.title}
                        onCommit={(title) => setTitle(item.productId, title)}
                      />
                      {priceMode && (
                        <MerchBasePriceInput
                          itemTitle={item.title}
                          valueCents={item.basePriceCents ?? null}
                          onCommit={(cents) => setBasePrice(item.productId, cents)}
                        />
                      )}
                      <MerchLinkInput
                        itemTitle={item.title}
                        value={item.linkUrl}
                        onCommit={(linkUrl) => setLink(item.productId, linkUrl)}
                      />
                    </div>
                    {!isTicker && (
                      <MerchVariantsEditor
                        item={item}
                        merchOverlayId={existing?.merchOverlayId ?? null}
                        auth0Token={auth0Token}
                        cartBase={variantCartBase}
                        onCommit={(variantOptions) => setVariantOptions(item.productId, variantOptions)}
                      />
                    )}
                    {!isTicker && (
                      <MerchItemPhotoStrip
                        item={item}
                        auth0Token={auth0Token}
                        onFeature={(url) => setFeaturedImage(item.productId, url)}
                        onAdded={(url) => addItemPhoto(item.productId, url)}
                        onRemove={(url) => removeItemPhotoFromDraft(item.productId, url)}
                        onUploadingChange={onPhotoUploadingChange}
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
            <p className='mt-2 text-[11px] text-dm-text-accent'>
              {itemOrderHint}
            </p>

            <ManualItemAdd existingIds={existingIds} atCap={atItemCap} showPhoto={!isTicker} onAdd={addItem} />
          </SectionCard>
          {!isTicker && (
            <SectionCard title='Shop mode'>
              <MerchStyleSwitch
                label='Shop mode'
                testId='merch-shop-mode'
                checked={draft.shopMode}
                onLabel='Labels instead of prices'
                offLabel='Prices and percentages'
                help='On, the board shows no prices, percentages or arrows — each row gets the label below, under the product name, in the product name&apos;s font. Rows keep their links. Off is exactly today&apos;s board.'
                onChange={(v) => setDraft((d) => ({ ...d, shopMode: v }))}
              />
              <label className='mt-4 block'>
                <FieldLabel>Row label</FieldLabel>
                <input
                  value={draft.shopLabelText}
                  onChange={(e) => setDraft((d) => ({ ...d, shopLabelText: e.target.value }))}
                  placeholder={DEFAULT_MERCH_SHOP_LABEL}
                  maxLength={MAX_SHOP_LABEL_LEN}
                  className={inputClass}
                />
              </label>
              <label className='mt-4 block'>
                <FieldLabel>Bottom call-to-action</FieldLabel>
                <input
                  value={draft.ctaText}
                  onChange={(e) => setDraft((d) => ({ ...d, ctaText: e.target.value }))}
                  placeholder='[ SHOP THE COLLECTION ]'
                  maxLength={MAX_CTA_TEXT_LEN}
                  className={inputClass}
                />
              </label>
            </SectionCard>
          )}

          {isEdit && priceMode && existing !== null && (
            <SectionCard title='Test mode'>
              <MerchTestModeControls
                board={existing}
                auth0Token={auth0Token}
                onBoardChanged={(overlay) => onChanged?.(overlay)}
                showHints
              />
            </SectionCard>
          )}

          {!isTicker && (
            <SectionCard title='Style'>
              <div className='mb-4 grid grid-cols-1 gap-3 md:grid-cols-2'>
                <MerchFontSelect
                  label='Title font'
                  value={draft.titleFontKey}
                  onChange={(v) => setDraft((d) => ({ ...d, titleFontKey: v }))}
                />
                <ColorInput
                  label='Title color'
                  value={draft.titleColor}
                  onChange={(v) => setDraft((d) => ({ ...d, titleColor: v }))}
                  solidOnly
                />
                <MerchFontSelect
                  label='Product name font'
                  value={draft.productTitleFontKey}
                  onChange={(v) => setDraft((d) => ({ ...d, productTitleFontKey: v }))}
                />
                <ColorInput
                  label='Product name color'
                  value={draft.productTitleColor}
                  onChange={(v) => setDraft((d) => ({ ...d, productTitleColor: v }))}
                  solidOnly
                />
                <MerchFontSelect
                  label='Price font'
                  value={draft.priceFontKey}
                  onChange={(v) => setDraft((d) => ({ ...d, priceFontKey: v }))}
                />
              </div>
              <div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
                <ColorInput
                  label='Board background'
                  value={draft.boardBgColor}
                  onChange={(v) => setDraft((d) => ({ ...d, boardBgColor: v === 'transparent' ? '#00000000' : v }))}
                />
                <ColorInput
                  label='Row background'
                  value={draft.rowBgColor}
                  onChange={(v) => setDraft((d) => ({ ...d, rowBgColor: v === 'transparent' ? '#00000000' : v }))}
                />
                <ColorInput
                  label='Photo tile background'
                  value={draft.tileBgColor}
                  onChange={(v) => setDraft((d) => ({ ...d, tileBgColor: v === 'transparent' ? '#00000000' : v }))}
                />
                <ColorInput
                  label='Outer frame'
                  value={draft.outerBgColor}
                  onChange={(v) => setDraft((d) => ({ ...d, outerBgColor: v === 'transparent' ? '#00000000' : v }))}
                />
              </div>
              <div className='mt-3 grid grid-cols-1 gap-3 md:grid-cols-2'>
                <MerchRadiusInput
                  label='Outer frame corner radius'
                  value={draft.outerCornerRadius}
                  onChange={(v) => setDraft((d) => ({ ...d, outerCornerRadius: v }))}
                />
                <MerchRadiusInput
                  label='Board corner radius'
                  value={draft.boardCornerRadius}
                  onChange={(v) => setDraft((d) => ({ ...d, boardCornerRadius: v }))}
                />
              </div>
              <div className='mt-3 grid grid-cols-1 gap-3 md:grid-cols-2'>
                <MerchTickerPxInput
                  label='Outer frame side padding (px)'
                  value={draft.outerPadX}
                  max={MAX_MERCH_BOARD_PAD_X_PX}
                  onChange={(v) => setDraft((d) => ({ ...d, outerPadX: v }))}
                />
                <MerchTickerPxInput
                  label='Board side padding (px)'
                  value={draft.boardPadX}
                  max={MAX_MERCH_BOARD_PAD_X_PX}
                  onChange={(v) => setDraft((d) => ({ ...d, boardPadX: v }))}
                />
              </div>
              <div className='mt-3 grid grid-cols-1 gap-3 md:grid-cols-2'>
                <MerchTickerPxInput
                  label='Outer frame top padding (px)'
                  value={draft.outerPadTop}
                  max={MAX_MERCH_BOARD_PAD_X_PX}
                  onChange={(v) => setDraft((d) => ({ ...d, outerPadTop: v }))}
                />
                <MerchTickerPxInput
                  label='Outer frame bottom padding (px)'
                  value={draft.outerPadBottom}
                  max={MAX_MERCH_BOARD_PAD_X_PX}
                  onChange={(v) => setDraft((d) => ({ ...d, outerPadBottom: v }))}
                />
              </div>
              <div className='mt-3 grid grid-cols-1 gap-3 md:grid-cols-2'>
                <MerchPhotoHeightInput
                  label='Product photo height (px)'
                  value={draft.productImageHeight}
                  onChange={(v) => setDraft((d) => ({ ...d, productImageHeight: v }))}
                />
                <ColorInput
                  label='Row border color'
                  value={draft.rowBorderColor}
                  onChange={(v) => setDraft((d) => ({ ...d, rowBorderColor: v === 'transparent' ? '#00000000' : v }))}
                />
              </div>

              <label className='mt-3 block'>
                <FieldLabel>Board background image URL</FieldLabel>
                <input
                  value={draft.boardBgImage}
                  onChange={(e) => setDraft((d) => ({ ...d, boardBgImage: e.target.value }))}
                  placeholder='https://… (a StagePilot library image)'
                  maxLength={MAX_LINK_LEN}
                  className={inputClass}
                />
              </label>

              <div className='mt-4'>
                <FieldLabel>Shop label &amp; call-to-action colors</FieldLabel>
                <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                  <ColorInput
                    label='Row label color'
                    value={draft.shopLabelColor}
                    onChange={(v) => setDraft((d) => ({ ...d, shopLabelColor: v === 'transparent' ? '#00000000' : v }))}
                  />
                  <ColorInput
                    label='Call-to-action color'
                    value={draft.ctaColor}
                    onChange={(v) => setDraft((d) => ({ ...d, ctaColor: v === 'transparent' ? '#00000000' : v }))}
                  />
                </div>
              </div>

              <div className='mt-3 grid grid-cols-1 gap-3 md:grid-cols-2'>
                <ColorInput
                  label='Outer frame border color'
                  value={draft.outerBorderColor}
                  onChange={(v) => setDraft((d) => ({ ...d, outerBorderColor: v === 'transparent' ? '#00000000' : v }))}
                />
                <MerchTickerPxInput
                  label='Outer frame border width (px, 0 = off)'
                  value={draft.outerBorderWidth}
                  max={MAX_MERCH_OUTER_BORDER_PX}
                  onChange={(v) => setDraft((d) => ({ ...d, outerBorderWidth: v }))}
                />
              </div>
              <div className='mt-4'>
                <FieldLabel>Closed state color</FieldLabel>
                <p className='mb-2 text-[11px] text-dm-text-accent'>
                  Twitch extension collapsed MERCH tab. Not shown in the
                  preview above.
                </p>
                <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                  <ColorInput
                    label='Outline'
                    value={draft.tabOutlineColor}
                    onChange={(v) => setDraft((d) => ({ ...d, tabOutlineColor: v === 'transparent' ? '#00000000' : v }))}
                  />
                  <ColorInput
                    label='Text'
                    value={draft.tabTextColor}
                    onChange={(v) => setDraft((d) => ({ ...d, tabTextColor: v === 'transparent' ? '#00000000' : v }))}
                  />
                </div>
              </div>

              <div className='mt-4'>
                <FieldLabel>Collapse button (✕)</FieldLabel>
                <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                  <MerchTickerPxInput
                    label='✕ size (px)'
                    value={draft.collapseXSize}
                    min={MIN_MERCH_COLLAPSE_X_PX}
                    max={MAX_MERCH_COLLAPSE_X_PX}
                    onChange={(v) => setDraft((d) => ({ ...d, collapseXSize: v }))}
                  />
                  <div className='grid grid-cols-[1fr_auto] items-end gap-3'>
                    <ColorInput
                      label='✕ color'
                      value={draft.collapseXColor}
                      onChange={(v) => setDraft((d) => ({ ...d, collapseXColor: v === 'transparent' ? '#00000000' : v }))}
                    />
                    <button
                      type='button'
                      data-testid='merch-collapse-x-color-reset'
                      disabled={draft.collapseXColor === ''}
                      onClick={() => setDraft((d) => ({ ...d, collapseXColor: '' }))}
                      className='rounded-md border border-white/10 px-3 py-2 text-xs text-dm-text-accent hover:border-brand hover:text-dm-text-primary disabled:cursor-default disabled:opacity-40 disabled:hover:border-white/10 disabled:hover:text-dm-text-accent'
                    >
                      Use the default
                    </button>
                  </div>
                </div>
              </div>

              <div className='mt-4'>
                <FieldLabel>Off-site link arrow</FieldLabel>
                <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                  <ColorInput
                    label='Arrow color'
                    value={draft.offsiteMarkColor}
                    onChange={(v) => setDraft((d) => ({ ...d, offsiteMarkColor: v === 'transparent' ? '#00000000' : v }))}
                  />
                  <div className='flex items-end'>
                    <button
                      type='button'
                      data-testid='merch-offsite-mark-reset'
                      disabled={draft.offsiteMarkColor === ''}
                      onClick={() => setDraft((d) => ({ ...d, offsiteMarkColor: '' }))}
                      className='rounded-md border border-white/10 px-3 py-2 text-xs text-dm-text-accent hover:border-brand hover:text-dm-text-primary disabled:cursor-default disabled:opacity-40 disabled:hover:border-white/10 disabled:hover:text-dm-text-accent'
                    >
                      Use the default
                    </button>
                  </div>
                </div>
              </div>

              <div className='mt-4'>
                <FieldLabel>Text glow</FieldLabel>
                <p className='mb-2 text-[11px] text-dm-text-accent'>
                  Soft glow behind the board&apos;s text — title, product
                  names, prices and arrows. The reference look is 4px at
                  white 25% — set the radius to 4 and keep the color at
                  the default. 0px turns it off entirely.
                </p>
                <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                  <MerchTickerPxInput
                    label='Text glow (px, 0 = off)'
                    value={draft.textGlowPx}
                    max={MAX_MERCH_TEXT_GLOW_PX}
                    onChange={(v) => setDraft((d) => ({ ...d, textGlowPx: v }))}
                  />
                  <ColorInput
                    label='Text glow color'
                    value={draft.textGlowColor}
                    onChange={(v) => setDraft((d) => ({ ...d, textGlowColor: v === 'transparent' ? '#ffffff00' : v }))}
                  />
                </div>
                <MerchStyleSwitch
                  label='Glow on arrows'
                  testId='merch-text-glow-arrows'
                  checked={draft.textGlowArrows}
                  onLabel='Arrows glow with the text'
                  offLabel='Arrows stay crisp'
                  help='Off removes the glow from the up/down arrows alone — prices, percentages and names keep it. Does nothing while the glow radius is 0.'
                  onChange={(v) => setDraft((d) => ({ ...d, textGlowArrows: v }))}
                />
              </div>
              <MerchChangeAnimationPicker
                value={draft.changeAnimation}
                tweenMs={draft.tweenMs}
                onChange={(v) => setDraft((d) => ({ ...d, changeAnimation: v }))}
                onTweenMsChange={(ms) => setDraft((d) => ({ ...d, tweenMs: ms }))}
              />
              <MerchStyleSwitch
                label='Show variant options'
                testId='merch-show-variants'
                checked={draft.showVariants}
                onLabel='Option boxes on hover'
                offLabel='Plain rows'
                help='Off hides the size/colour boxes on every row, on the overlay and the Twitch extension alike — including boards whose options only appear once a live feed starts. Rows then link to the product itself.'
                onChange={(v) => setDraft((d) => ({ ...d, showVariants: v }))}
              />
              <MerchStyleSwitch
                label='Square product photos'
                testId='merch-square-photos'
                checked={draft.squarePhotos}
                onLabel='Square, whole photo fits'
                offLabel='Tall photo strip'
                help='On, each photo sits in a square the width of the photo column with the whole image fitted inside it — nothing cropped. The space around it stays whatever the photo background already is, so a transparent photo on a transparent background stays transparent.'
                onChange={(v) => setDraft((d) => ({ ...d, squarePhotos: v }))}
              />
            </SectionCard>
          )}
          {isTicker && (
            <MerchTickerStyleSection
              style={draft.tickerStyle}
              onChange={(patch) => setDraft((d) => ({ ...d, tickerStyle: { ...d.tickerStyle, ...patch } }))}
            />
          )}
          {isTicker && (
            <MerchTickerMessagesSection
              messages={draft.messages}
              onChange={(next) => setDraft((d) => ({ ...d, messages: next }))}
            />
          )}
        </fieldset>
      </div>
      <div className='lg:col-span-1'>
        <div className='sticky top-4 space-y-3'>
          <div className='flex items-center gap-2'>
            <button
              type='submit'
              disabled={submitting || !draftValid || photoUploading}
              title={photoUploading ? 'A photo is still uploading' : undefined}
              className='inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
            >
              {submitting && <LoadingSpinner />}
              {isEdit ? 'Save changes' : `Create ${styleNoun}`}
            </button>
            <button
              type='button'
              onClick={onCancel}
              disabled={submitting}
              className='inline-flex flex-1 items-center justify-center rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:opacity-60'
            >
              Cancel
            </button>
          </div>
          {error !== null && <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>}
          {savedNote && <SavedNote testId='merch-overlay-saved' />}

          {isEdit && (
            <>
              <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
                Browser source URL
              </h4>
              <OverlayLinkBlock link={overlayLink} label={styleNoun} />
              <p className='text-[11px] text-dm-text-accent'>
                {isTicker
                  ? 'Saving republishes the live ticker — open sources pick up config and message edits without a refresh (when a store is bound).'
                  : 'Saving republishes the live board — open sources pick up config edits without a refresh (when a store is bound).'}
              </p>
            </>
          )}

          {existing !== null && !isTicker && (
            <div className='space-y-1.5' data-testid='merch-board-id-row'>
              <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
                Board ID (Twitch extension)
              </h4>
              <div className='flex items-center justify-between gap-2 rounded-lg border border-white/5 bg-dm-bg-accent/30 px-3 py-2'>
                <span className='min-w-0 select-all truncate font-mono text-[11px] text-dm-text-primary'>
                  {existing.merchOverlayId}
                </span>
                <CopyButton value={existing.merchOverlayId} label='board ID' />
              </div>
              <p className='text-[11px] text-dm-text-accent'>
                The Twitch extension&apos;s configuration page asks for this
                ID to pair your channel with this board.
              </p>
            </div>
          )}
          {!isEdit && (
            <p className='text-[11px] text-dm-text-accent'>
              The browser-source link appears here (and in the list) after the
              first save.
            </p>
          )}

          <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
            {isTicker ? 'Preview (design + speed, not live data)' : 'Preview'}
          </h4>
          {!isTicker && <MerchPreview draft={draft} />}
          {isTicker && <MerchTickerPreview draft={draft} />}
          {isTicker && (
            <p className='text-[11px] text-dm-text-accent'>
              Sample quotes (+5.4% / −2.2% / 0.0%) and placeholder messages
              stand in until you add items and messages on stream the
              bound store&apos;s live prices drive the top bar. Scrolls at
              the draft speed; the page background applies to the
              standalone browser source only.
            </p>
          )}
        </div>
      </div>

      {pickerOpen && auth0Token !== undefined && (
        <ShopifyProductPickerModal
          shop={draft.shopDomain}
          auth0Token={auth0Token}
          existingIds={existingIds}
          atCap={atItemCap}
          photoless={isTicker}
          onAdd={addItem}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </form>
  )
}

const MerchListRow = ({
  overlay,
  onEdit,
  onDelete,
  onToggleExtension,
  busy,
  extensionBusy
}: {
  overlay: MerchOverlaySummary
  onEdit: () => void
  onDelete: () => void
  onToggleExtension: () => void
  busy: boolean
  extensionBusy: boolean
}): JSX.Element => {
  const overlayLink = useAccountOverlayLink('merch', overlay.merchOverlayId)
  const noun = merchDisplayStyleOf(overlay) === 'TICKER' ? 'merch ticker' : 'merch board'
  return (
    <li className='rounded-lg border border-white/5 bg-dm-bg-accent/30 px-4 py-3'>
      <div className='flex items-center justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-3'>
          <span className='shrink-0 rounded-full bg-dm-bg-accent/50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
            {merchDisplayStyleOf(overlay) === 'TICKER' ? 'Merch ticker' : 'Merch board'}
          </span>
          <div className='flex min-w-0 flex-col leading-tight'>
            <span className='truncate text-sm font-medium text-dm-text-primary'>
              {overlay.name}
            </span>
            <span className='truncate text-[11px] text-dm-text-accent'>
              {overlay.storeTitle} · {overlay.items.length} item{overlay.items.length === 1 ? '' : 's'} · {overlay.shopDomain ?? 'no store bound'}
              {merchDisplayStyleOf(overlay) !== 'TICKER' && !merchExtensionVisibleOf(overlay) && ' · hidden on Twitch'}
              {merchDisplayStyleOf(overlay) !== 'TICKER' && merchExtensionChannelLoginOf(overlay) !== '' && (
                <span
                  data-testid='merch-list-extension-channel'
                  title={`Twitch channel id ${merchExtensionChannelIdOf(overlay)}`}
                >
                  {' '}· Twitch: {merchExtensionChannelLoginOf(overlay)}
                </span>
              )}
            </span>
          </div>
        </div>
        <div className='flex shrink-0 items-center gap-1.5'>
          <OverlayLinkRowActions link={overlayLink} label={`${overlay.name} ${noun}`} />

          {merchDisplayStyleOf(overlay) !== 'TICKER' && (
            <MerchExtensionQuickToggle overlay={overlay} busy={extensionBusy} onToggle={onToggleExtension} />
          )}
          <button
            type='button'
            onClick={onEdit}
            title='Edit'
            disabled={busy}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            <PencilSquareIcon className='size-3.5' aria-hidden='true' />
            Edit
          </button>
          <button
            type='button'
            onClick={onDelete}
            disabled={busy}
            title='Delete'
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy ? <LoadingSpinner /> : <TrashIcon className='size-3.5' aria-hidden='true' />}
            Delete
          </button>
        </div>
      </div>
    </li>
  )
}

export const MerchOverlayManager = ({
  view,
  basePath,
  backLabel,
  superuser
}: {
  view: QuickOverlayManagerView
  basePath: string
  backLabel: string
  superuser: boolean
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const navigate = useNavigate()
  const { search } = useLocation()
  const createStyle: MerchDisplayStyle =
    new URLSearchParams(search).get('style') === 'ticker' ? 'TICKER' : 'BOARD'

  const [overlays, setOverlays] = useState<MerchOverlaySummary[] | null>(null)
  const [listBusy, setListBusy] = useState(false)
  const [listError, setListError] = useState<string | null>(null)

  const loadOverlays = useCallback(async (): Promise<void> => {
    if (auth0Token === undefined) return
    setListBusy(true)
    setListError(null)
    try {
      const { overlays: fetched } = await graphqlRequest<{ overlays: MerchOverlaySummary[] }>(
        listMyMerchOverlaysQuery, {}, auth0Token
      )
      setOverlays(fetched)
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load merch boards')
    } finally {
      setListBusy(false)
    }
  }, [auth0Token])

  useEffect(() => {
    void loadOverlays()
  }, [loadOverlays])

  const [rowBusyId, setRowBusyId] = useState<string | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)
  const [extBusyId, setExtBusyId] = useState<string | null>(null)

  const goList = (): void => { void navigate(basePath) }
  const goCreate = (): void => { void navigate(`${basePath}/new`) }
  const goCreateTicker = (): void => { void navigate(`${basePath}/new?style=ticker`) }
  const goEdit = (merchOverlayId: string): void => { void navigate(`${basePath}/${merchOverlayId}`) }

  const editingId = typeof view === 'object' ? view.edit : null
  const editing = useMemo<MerchOverlaySummary | null>(() => {
    if (editingId === null || overlays === null) return null
    return overlays.find((o) => o.merchOverlayId === editingId) ?? null
  }, [editingId, overlays])
  const editLink = useAccountOverlayLink('merch', editing === null ? null : editing.merchOverlayId)

  const upsertOverlay = (overlay: MerchOverlaySummary): void => {
    setOverlays((prev) => {
      if (prev === null) return [overlay]
      const exists = prev.some((o) => o.merchOverlayId === overlay.merchOverlayId)
      return exists
        ? prev.map((o) => o.merchOverlayId === overlay.merchOverlayId ? overlay : o)
        : [...prev, overlay]
    })
  }

  const removeOverlay = (merchOverlayId: string): void => {
    setOverlays((prev) => prev === null ? prev : prev.filter((o) => o.merchOverlayId !== merchOverlayId))
  }

  const handleToggleExtension = async (overlay: MerchOverlaySummary): Promise<void> => {
    if (auth0Token === undefined || extBusyId !== null || rowBusyId !== null) return
    setExtBusyId(overlay.merchOverlayId)
    setRowError(null)
    try {
      const { overlay: saved } = await graphqlRequest<{ overlay: MerchOverlaySummary }>(
        updateMerchOverlayVisibilityMutation,
        { merchOverlayId: overlay.merchOverlayId, extensionVisible: !merchExtensionVisibleOf(overlay) },
        auth0Token
      )
      upsertOverlay(saved)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to update the Twitch extension visibility')
    } finally {
      setExtBusyId(null)
    }
  }

  const handleDelete = async (overlay: MerchOverlaySummary): Promise<void> => {
    if (auth0Token === undefined || rowBusyId !== null || extBusyId !== null) return
    const ok = globalThis.confirm(
      `Delete ${merchDisplayStyleOf(overlay) === 'TICKER' ? 'merch ticker' : 'merch board'} "${overlay.name}"? Any browser source still pointing at its URL goes permanently blank. Sales history for the store is kept.`
    )
    if (!ok) return
    setRowBusyId(overlay.merchOverlayId)
    setRowError(null)
    try {
      const { result } = await graphqlRequest<{ result: DeletionResult }>(
        deleteMerchOverlayMutation, { merchOverlayId: overlay.merchOverlayId }, auth0Token
      )
      if (result.deleted) removeOverlay(overlay.merchOverlayId)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to delete merch board')
    } finally {
      setRowBusyId(null)
    }
  }

  if (view === 'create') {
    return (
      <MerchOverlayForm
        existing={null}
        displayStyle={createStyle}
        auth0Token={auth0Token}
        overlayLink={editLink}
        onSaved={(overlay) => {
          upsertOverlay(overlay)
          void navigate(`${basePath}/${overlay.merchOverlayId}`, { replace: true })
        }}
        onCancel={goList}
        backLabel={backLabel}
        superuser={superuser}
      />
    )
  }

  if (typeof view === 'object') {
    if (overlays === null) {
      return (
        <div className='mt-4 flex items-center justify-center px-4 pb-4 py-10'>
          <LoadingSpinner />
        </div>
      )
    }
    if (editing === null) {
      return (
        <div className='mt-4 px-4 pb-4'>
          <ErrorInline>That merch board no longer exists.</ErrorInline>
          <button onClick={goList} className='mt-3 rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:text-dm-text-primary'>
            {backLabel}
          </button>
        </div>
      )
    }
    return (
      <MerchOverlayForm
        existing={editing}
        displayStyle={merchDisplayStyleOf(editing)}
        auth0Token={auth0Token}
        overlayLink={editLink}
        onSaved={upsertOverlay}
        onChanged={upsertOverlay}
        onCancel={goList}
        backLabel={backLabel}
        superuser={superuser}
      />
    )
  }

  let listContent: JSX.Element
  if (overlays === null) {
    listContent = (
      <div className='flex items-center justify-center rounded-lg border border-dashed border-white/10 px-4 py-10'>
        <LoadingSpinner />
      </div>
    )
  } else if (overlays.length === 0) {
    listContent = (
      <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/10 px-4 py-10 text-center text-sm text-dm-text-secondary'>
        <BuildingStorefrontIcon className='size-8 text-dm-text-accent' aria-hidden='true' />
        <p>No merch boards or tickers yet.</p>
        <div className='flex flex-wrap items-center justify-center gap-2'>
          <button
            type='button'
            onClick={goCreate}
            className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80'
          >
            <PlusIcon className='size-3.5' aria-hidden='true' />
            Create a merch board
          </button>
          <button
            type='button'
            onClick={goCreateTicker}
            className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80'
          >
            <PlusIcon className='size-3.5' aria-hidden='true' />
            Create a merch ticker
          </button>
        </div>
      </div>
    )
  } else {
    listContent = (
      <ul className='space-y-2' data-testid='merch-overlays-list'>
        {sortByName(overlays, (o) => o.name, (o) => o.merchOverlayId).map((overlay) => (
          <MerchListRow
            key={overlay.merchOverlayId}
            overlay={overlay}
            onEdit={() => goEdit(overlay.merchOverlayId)}
            onDelete={() => { void handleDelete(overlay) }}
            onToggleExtension={() => { void handleToggleExtension(overlay) }}
            busy={rowBusyId === overlay.merchOverlayId}
            extensionBusy={extBusyId === overlay.merchOverlayId}
          />
        ))}
      </ul>
    )
  }

  return (
    <div className='mt-2 flex flex-col gap-1.5 px-4 pb-4'>
      <SectionCard
        title='Merch Boards & Tickers'
        action={
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={() => { void loadOverlays() }}
              disabled={listBusy}
              title='Refresh'
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
            >
              {listBusy ? <LoadingSpinner /> : <ArrowPathIcon className='size-3.5' aria-hidden='true' />}
              Refresh
            </button>
            <button
              type='button'
              data-testid='add-merch-overlay-button'
              onClick={goCreate}
              className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80'
            >
              <PlusIcon className='size-3.5' aria-hidden='true' />
              Add new merch board
            </button>
            <button
              type='button'
              data-testid='add-merch-ticker-button'
              onClick={goCreateTicker}
              className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80'
            >
              <PlusIcon className='size-3.5' aria-hidden='true' />
              Add new merch ticker
            </button>
          </div>
        }
      >
        {listError !== null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setListError(null)}>{listError}</ErrorInline>
          </div>
        )}
        {rowError !== null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setRowError(null)}>{rowError}</ErrorInline>
          </div>
        )}

        {listContent}

        <div className='mt-4 space-y-1 text-[11px] text-dm-text-accent'>
          <p>
            Each merch board has its own browser-source URL. Add it to your streaming software with a transparent background.
          </p>
          <p>{OVERLAY_LINK_NOTE}</p>
        </div>
      </SectionCard>
    </div>
  )
}
